import { useSyncExternalStore } from 'react';
import { EN_BEREICHE } from './i18n-en/index.js';

/**
 * Zweisprachigkeit ohne Grossumbau.
 *
 * Der uebliche Weg waere, jeden Text durch einen Schluessel zu ersetzen
 * (`t('autopilot.title')`) und beide Sprachen in Woerterbuecher zu
 * schreiben. Bei sechzehntausend Zeilen Oberflaeche heisst das: jede Datei
 * anfassen, tausend Schluessel erfinden, und bis alles uebersetzt ist,
 * zeigt die Anwendung Schluessel statt Text.
 *
 * Hier ist der SCHLUESSEL DER DEUTSCHE SATZ. Daraus folgt alles Gute:
 *
 *   - Deutsch funktioniert weiter, auch ohne einen einzigen Eintrag.
 *   - Fehlt eine Uebersetzung, erscheint der deutsche Satz -- nie ein
 *     kryptischer Schluessel und nie eine leere Flaeche.
 *   - Uebersetzen heisst: Zeilen in EINE Datei nachtragen. Man kann heute
 *     mit dem Anmeldebildschirm anfangen und morgen weitermachen.
 *
 * Der Preis: Aendert jemand den deutschen Text, greift die Uebersetzung
 * nicht mehr. Das faellt auf (der Satz steht dann deutsch da) und laesst
 * sich mit `npm run i18n:fehlend` finden, statt still falsch zu sein.
 */

export type Sprache = 'de' | 'en';

const SPEICHER_SCHLUESSEL = 'aff.sprache';

/**
 * Beim ersten Aufruf die Sprache des Browsers uebernehmen.
 *
 * Wer die Anwendung auf Englisch erwartet, soll sie nicht erst umstellen
 * muessen -- und wer Deutsch spricht, merkt von der Erkennung nichts.
 */
function ersteSprache(): Sprache {
  try {
    const gespeichert = localStorage.getItem(SPEICHER_SCHLUESSEL);
    if (gespeichert === 'de' || gespeichert === 'en') return gespeichert;
  } catch {
    /* Privater Modus: dann eben die Browsersprache. */
  }
  return navigator.language?.toLowerCase().startsWith('de') ? 'de' : 'en';
}

let aktuell: Sprache = ersteSprache();
const hoerer = new Set<() => void>();

export function sprache(): Sprache {
  return aktuell;
}

export function setzeSprache(neu: Sprache): void {
  if (neu === aktuell) return;
  aktuell = neu;
  try { localStorage.setItem(SPEICHER_SCHLUESSEL, neu); } catch { /* egal */ }
  document.documentElement.lang = neu;
  for (const h of hoerer) h();
}

/** Abonniert die Sprachumschaltung, damit React neu zeichnet. */
export function useSprache(): Sprache {
  return useSyncExternalStore(
    (h) => { hoerer.add(h); return () => hoerer.delete(h); },
    () => aktuell,
    () => aktuell,
  );
}

/**
 * Englische Fassungen, Schluessel ist der deutsche Satz.
 *
 * Nach Bereichen sortiert, damit man beim Nachtragen sieht, was zusammen
 * gehoert -- und damit ein halb uebersetzter Bereich auffaellt.
 */
export const EN: Record<string, string> = {
  'Prüfe zuerst dein Plattformkonto. Bestätige nur, wenn dort kein Beitrag erschienen ist. Ein erneuter Versuch kann sonst doppelt posten.': 'Check your platform account first. Confirm only if no post appeared. Retrying can otherwise publish twice.',
  'Erneuter Versuch abgebrochen.': 'Retry cancelled.',
  'TikTok verlangt die Freigabe jedes Beitrags, auch bei Vollautomatik. Instagram kann freigegebene Bilder, Karussells oder Videos veröffentlichen. Musik aus der Bibliothek wird in Videos eingebrannt; Bildposts übernehmen sie nicht.': 'TikTok requires approval for each post, including in full automation. Instagram can publish approved images, carousels or videos. Library music is embedded in videos; image posts do not include it.',
  'Status ungeklärt – Plattformkonto prüfen': 'Status unknown – check platform account',
  'Deine Daten': 'Your data',
  'Daten als JSON exportieren': 'Export data as JSON',
  'Die Kontolöschung entfernt Projekte, Inhalte und Medien. Beende vorher dein Abonnement. Abrechnungsdaten bleiben im Rahmen der Aufbewahrungspflichten erhalten.': 'Deleting your account removes projects, content and media. End your subscription first. Billing records are retained as legally required.',
  'Zum Löschen deine E-Mail-Adresse eingeben': 'Enter your email address to delete your account',
  'Konto und Inhalte endgültig löschen': 'Permanently delete account and content',

  'E-Mail bestätigt. Du kannst dich jetzt anmelden.': 'Email verified. You can now sign in.',
  'Passwort geändert. Melde dich mit dem neuen Passwort an.': 'Password changed. Sign in with your new password.',
  'Prüfe dein E-Mail-Postfach und bestätige deine Adresse vor der Anmeldung.': 'Check your inbox and verify your email before signing in.',
  'Trage zuerst deine E-Mail-Adresse ein.': 'Enter your email address first.',
  'Falls die Adresse zu einem passenden Konto gehört, erhältst du eine E-Mail.': 'If the email matches an eligible account, you will receive an email.',
  'Neues Passwort speichern': 'Save new password',
  'Passwort vergessen': 'Forgot password',
  'Bestätigung erneut senden': 'Resend verification',
  'Für die Erstellung benötigst du gekaufte Credits oder ein Guthabenpaket.': 'Content creation requires purchased credits or a credit package.',

  /* --- Allgemein --- */
  'Willkommen, {name}! Dein Startguthaben liegt bereit.': 'Welcome, {name}! Your starter credits are ready.',
  'Das hat nicht geklappt.': 'That didn’t work.',

  /* --- Navigation und Rahmen --- */
  'Projekte': 'Projects',
  'Freigaben': 'Approvals',
  'Musik': 'Music',
  'Guthaben & Tarif': 'Credits & plan',
  'Einstellungen': 'Settings',
  'Übersicht': 'Overview',
  'Plan & Queue': 'Plan & queue',
  'Agent': 'Agent',
  'Agenten': 'Agents',
  'Autopilot': 'Autopilot',
  'Verbindungen': 'Connections',
  'Assistent': 'Assistant',
  'Abmelden': 'Sign out',
  'Credits': 'Credits',
  'fast leer': 'almost empty',
  'Betreiber': 'Operator',
  'Impressum': 'Legal notice',
  'Nutzungsbedingungen': 'Terms',
  'Datenschutz': 'Privacy',
  'Widerruf': 'Withdrawal',
  'Verträge kündigen': 'Cancel contracts',
  'Projekt wechseln': 'Switch project',
  'Alle Projekte': 'All projects',

  /* --- Anmeldung --- */
  'Anmelden': 'Sign in',
  'Konto anlegen': 'Create account',
  'E-Mail': 'Email',
  'Passwort': 'Password',
  'Name': 'Name',
  'Mit Google anmelden': 'Continue with Google',
  'Passwort vergessen?': 'Forgot your password?',

  /* --- Wiederkehrende Knöpfe --- */
  'Speichern': 'Save',
  'Abbrechen': 'Cancel',
  'Löschen': 'Delete',
  '„{name}" löschen?': '"{name}" — delete?',
  'Schließen': 'Close',
  'Zurück': 'Back',
  'Weiter': 'Next',
  'Ansehen': 'View',
  'Freigeben': 'Approve',
  'Laufen lassen': 'Run now',
  'Neu laden': 'Reload',
  'Ja, machen': 'Yes, do it',
  'Alle': 'All',
  'Entwürfe': 'Drafts',
  'Freigegeben': 'Approved',
  'Geplant': 'Scheduled',
  'Veröffentlicht': 'Published',
  'Fehler': 'Error',

  /* --- Autopilot --- */
  'Autopilot aus': 'Autopilot off',
  'Vollautomatik': 'Fully automatic',
  'Autopilot mit Freigabe': 'Autopilot with approval',
  'Nächster Lauf': 'Next run',
  'Der Autopilot wartet': 'The autopilot is waiting',
  'Noch kein Konto verbunden': 'No account connected yet',
  'Wohin wird veröffentlicht?': 'Where does it publish?',
  'Betriebsart': 'Mode',

  /* --- Agent --- */
  'Wie viele Posts baut ein Lauf von Hand?': 'How many posts does a manual run build?',
  'Was baut er?': 'What does it build?',
  'Nur Bilder': 'Images only',
  'Meist Bilder': 'Mostly images',
  'Halbe-halbe': 'Half and half',
  'Nur Videos': 'Videos only',
  'Szenen pro Bild-Post': 'Scenes per image post',
  'Was steht im Bild?': 'What does the text say?',
  'Hashtags pro Post': 'Hashtags per post',
  'Bildmodell': 'Image model',
  'Videomodell': 'Video model',
  'Bildstil': 'Image style',
  'Stil wechseln': 'Change style',
  'Auswahl schließen': 'Close selection',
  'Eigene Anweisung': 'Custom instruction',
  'Serien': 'Series',
  'AI entscheidet': 'AI decides',
  'Immer Serie': 'Always a series',
  'Nie': 'Never',
  'Wie im Projekt': 'As in the project',
  'Eigener Prompt': 'Custom prompt',

  /* --- Katalog: Bildstil-Kategorien --- */
  'Echt & Fotografie': 'Real & photographic',
  'wirkt wie aufgenommen, nicht gerendert': 'looks captured, not rendered',
  'Anime & Manga': 'Anime & manga',
  'die grossen Handschriften, jede erkennbar': 'the great signature styles, each unmistakable',
  'Gezeichnet & Gemalt': 'Drawn & painted',
  'Hand, Pinsel, Filzstift': 'hand, brush, marker',
  'Druck & Handgemacht': 'Print & handmade',
  'Papier, Farbe, Knete — greifbar': 'paper, paint, clay — tangible',

  /* --- Katalog: Bildstile (Beschriftung + Hinweis) --- */
  'Blitzlicht-Schnappschuss': 'Flash Snapshot',
  'wie mit dem Handy fotografiert, nachts, echt': 'shot on a phone at night, unfiltered and real',
  'Analogfilm bei Tag': 'Analog Film, Daylight',
  'Korn, gedeckte Farben, Erinnerung': 'grain, muted colors, a memory',
  'Filmstill': 'Film Still',
  'ein Licht, tiefe Schatten, Kinoformat': 'one light source, deep shadows, widescreen',
  'Unscharf & körnig': 'Soft & Grainy',
  'weich, verträumt, viel Platz für Text': 'soft, dreamy, plenty of room for text',
  'Schwarzweiß-Reportage': 'Black & White Reportage',
  'harter Kontrast, Korn, zeitlos': 'hard contrast, grain, timeless',
  'Neonnacht': 'Neon Night',
  'Leuchtreklame, nasser Asphalt, Magenta & Cyan': 'neon signs, wet asphalt, magenta & cyan',
  'Nahaufnahme': 'Close-Up',
  'nah dran, alles dahinter cremig unscharf': 'shot close, everything behind melts into blur',
  'Studio-Licht': 'Studio Light',
  'weich ausgeleuchtet, ruhig, sauber': 'softly lit, calm, clean',
  'leuchtende Himmel, Lens Flares, Sehnsucht': 'glowing skies, lens flares, longing',
  'Gouache, warmes Tageslicht, handgemalt': 'gouache, warm daylight, hand-painted',
  'zart, weiches Licht, viel Luft': 'delicate, soft light, lots of breathing room',
  'sauber, kräftig, moderner Serien-Look': 'clean, bold, modern series look',
  'realistisch, Alltag, leise unheimlich': 'realistic, everyday, quietly unsettling',
  '80er-Zelluloid, Neon, nasse Nachtstadt': '80s cel animation, neon, a rain-slicked night city',
  'Anime · 90er': 'Anime · 90s',
  'Filmkorn, gedeckte Farben, VHS-Ära': 'film grain, muted colors, the VHS era',
  'Glitzer, Blüten, Pastell, grosse Augen': 'sparkle, blossoms, pastel, big eyes',
  'Pastell-Illustration': 'Pastel Illustration',
  'matt, ruhig, gedeckte Töne': 'matte, calm, muted tones',
  'Filzstift-Kritzelei': 'Marker Doodle',
  'wackelig, handgemacht, weisses Papier': 'wobbly, handmade, white paper',
  'Mystisch & Okkult': 'Mystic & Occult',
  'Pflaume, Gold, Mondphasen, Ornament': 'plum, gold, moon phases, ornament',
  'zwei Farben, Fehldruck, grobes Raster': 'two colors, misregistration, coarse halftone',
  'eine Form, eine Farbe, viel Fläche': 'one shape, one color, lots of open space',
  'ausgerissen, geklebt, Klebeband': 'torn paper, glue, tape',
  'Knete & Stop-Motion': 'Clay & Stop-Motion',
  'Fingerabdrücke, kleine Bühne, sympathisch': 'fingerprints, a small set, charming',
  'kein fester Look, passend zum Thema': 'no fixed look, matched to the topic',
  'Kein fester Look. Die AI wählt für jeden Post die Bildsprache, die zum Thema passt. Innerhalb eines Posts bleibt sie einheitlich. Meist die beste Wahl, wenn du noch keine Marke hast.':
    'No fixed look. The AI picks the visual style that fits each post’s topic, and keeps it consistent within that post. Usually the best choice if you don’t have a brand look yet.',

  /* --- Katalog: Text-Stile --- */
  'Gemischt': 'Mixed',
  'die AI wählt je Post, empfohlen': 'the AI picks per post, recommended',
  'Aussage': 'Statement',
  'ein Fakt, der überrascht': 'a fact that surprises',
  'Frage': 'Question',
  'lädt zum Kommentieren ein': 'invites a comment',
  'These': 'Claim',
  'eine Behauptung, die widerspricht': 'a claim people want to argue with',
  'Zahl': 'Number',
  '„3 Dinge, die …"': '"3 things that …"',
  'Mini-Geschichte': 'Mini Story',
  'EIN Post, seine Szenen nacheinander': 'ONE post, its scenes one after another',

  /* --- Katalog: Ziele --- */
  'Reichweite': 'Reach',
  'Möglichst viele Menschen sehen die Videos. Der Hook zählt mehr als die App. Sie kommt beiläufig vor.':
    'As many people as possible see the videos. The hook matters more than the app — it shows up in passing.',
  'Jeder Post endet mit einem konkreten Grund, die App jetzt zu laden.':
    'Every post ends with a concrete reason to download the app right now.',
  'Follower, Kommentare, Wiedererkennung. Posts fragen und laden zum Antworten ein.':
    'Followers, comments, recognition. Posts ask questions and invite replies.',
  'Etwas Neues steht an. Countdown, Einblicke, Vorfreude.':
    'Something new is coming. Countdown, behind-the-scenes, anticipation.',
  'Vertrauen': 'Trust',
  'Zeigen, wie es wirklich funktioniert. Nachvollziehbar, ohne Übertreibung.':
    'Show how it really works. Believable, without exaggeration.',
  'Bestandsnutzer': 'Existing users',
  'Funktionen zeigen, die viele übersehen. Für Leute, die die App schon haben.':
    'Show features that many overlook. For people who already have the app.',

  /* --- Veroeffentlichungsziele (TARGET_LABEL in format.ts) --- */
  'TikTok Fotos': 'TikTok Photos',

  /* --- Katalog: Tonalität (nur Anzeige, der gespeicherte Wert bleibt deutsch) --- */
  'Locker': 'Casual',
  'Sachlich': 'Matter-of-fact',
  'Frech': 'Cheeky',
  'Inspirierend': 'Inspiring',
  'Technisch': 'Technical',
  'Duzen, kurze Sätze, Augenzwinkern': 'Informal address, short sentences, a wink',
  'nüchtern, präzise, keine Superlative': 'plain, precise, no superlatives',
  'direkt, mit Haltung, darf anecken': 'direct, opinionated, allowed to ruffle feathers',
  'warm, ermutigend, Blick nach vorn': 'warm, encouraging, forward-looking',
  'Fachbegriffe erwünscht, Genauigkeit zuerst': 'technical terms welcome, precision first',
  'Tonfall': 'Tone',
  'wie im Projekt': 'as in the project',

  /* --- Katalog: CTA- und Feature-Vorschläge (Wert UND Anzeige, siehe i18n.ts) --- */
  'App laden. Link in Bio.': 'Get the app. Link in bio.',
  'Kostenlos starten. Link in Bio.': 'Start for free. Link in bio.',
  'Folg mir für mehr davon.': 'Follow me for more like this.',
  'Speichern, damit du es wiederfindest.': 'Save this so you can find it again.',
  'Schreib deine Antwort in die Kommentare.': 'Write your answer in the comments.',
  'Jetzt ausprobieren, dauert zwei Minuten.': 'Try it now, takes two minutes.',
  'Kostenlos starten': 'Free to start',
  'Ohne Werbung': 'No ads',
  'Offline nutzbar': 'Works offline',
  'Keine Anmeldung nötig': 'No sign-up required',
  'Fortschritt & Statistiken': 'Progress & stats',
  'Erinnerungen': 'Reminders',
  'Cloud-Sync über alle Geräte': 'Cloud sync across devices',
  'Widgets für den Homescreen': 'Home screen widgets',
  'AI-gestützte Vorschläge': 'AI-powered suggestions',
  'Daten bleiben auf dem Gerät': 'Data stays on the device',
  'Export als PDF': 'Export as PDF',
  'Mehrsprachig': 'Multilingual',
  'Anfänger ohne Vorwissen': 'Beginners with no prior knowledge',
  'Berufstätige mit wenig Zeit': 'Working professionals with little time',
  'Studierende': 'Students',
  'Eltern': 'Parents',
  'Profis, die schneller werden wollen': 'Professionals who want to get faster',
  'Menschen zwischen 20 und 35': 'People between 20 and 35',
  'Leute, die es schon oft vergeblich versucht haben': 'People who have tried before and given up',

  /* --- Status-Vokabular (statusLabel) und Ziel-Kurznamen --- */
  'ohne Termin': 'no due date',
  'wird gebaut': 'being built',
  'wird gepostet': 'being posted',
  'Idee': 'Idea',
  'Entwurf': 'Draft',
  'freigegeben': 'approved',
  'rendert': 'rendering',
  'geplant': 'scheduled',
  'veröffentlicht': 'published',
  'fehlgeschlagen': 'failed',
  'verworfen': 'discarded',

  /* --- Rest der Anwendung, nach und nach ergänzt (Übersetzungs-Sprint) --- */
  'Ging nicht': 'Didn’t work',
  'nicht eingeplant': 'not scheduled',
  'Hochladen ging nicht': 'Upload failed',
  'Da ist etwas schiefgegangen.': 'Something went wrong.',
  'Hat nicht geklappt': 'Didn’t work',
  'Zum Agenten': 'Go to agent',
  'Alle offenen Entwürfe von': 'All open drafts from',
  'kein Limit': 'no limit',
  'In der Queue': 'In the queue',
  'über alle Konten': 'across all accounts',
  'wartet auf Freigabe oder Termin': 'waiting for approval or a scheduled time',
  'Agent gelöscht.': 'Agent deleted.',
  'Die bereits gebauten Posts bleiben erhalten. Nur das Rezept verschwindet.': 'Posts already built are kept. Only the recipe is removed.',
  'Alle Agenten': 'All agents',
  'Überlegt sich Ideen…': 'Coming up with ideas…',
  'Frühere Läufe': 'Previous runs',
  'Sitzung wird geprüft…': 'Checking your session…',
  'Läuft nicht von selbst': 'Doesn’t run on its own',
  'noch nie gelaufen': 'never run yet',
  ', geschätzt': ', estimated',
  'Ein Rezept: wie die Posts aussehen sollen. Wann sie laufen, entscheidet der Fahrplan.': 'A recipe: how the posts should look. The schedule decides when they run.',
  'noch nichts beschrieben': 'nothing described yet',
  'kein fester Look, die AI wählt je Post': 'no fixed look, the AI picks per post',
  'Im Projekt ist kein fester Look gesetzt, die AI wählt je Post': 'The project has no fixed look set, the AI picks per post',
  'Dieser Agent nutzt den Stil, der im Projekt eingestellt ist.': 'This agent uses the style set on the project.',
  'der Look aus den Projekteinstellungen': 'the look from the project settings',
  'Wofür ist der da?': 'What is this for?',
  'nur für dich, taucht nirgends im Post auf': 'only for you, never appears in the post',
  'z. B. für Reichweite, ohne Produkt': 'e.g. for reach, without the product',
  'baut keine Videos — wird nicht gebraucht': 'doesn’t build videos — not needed',
  'baut keine Bilder — wird nicht gebraucht': 'doesn’t build images — not needed',
  '✓ hält die Bildwelt über mehrere Szenen durch': '✓ keeps the visual world consistent across scenes',
  'nimmt keine Bildvorlage an — bei mehrszenigen Posts unterscheiden sich die Gesichter': 'doesn’t accept a reference image — faces differ across scenes in multi-scene posts',
  'günstigstes verfügbares': 'cheapest available',
  'schreibt Ideen, Hooks, Textzeilen und Captions. Stärker = bessere Texte, teurer je Lauf.': 'writes ideas, hooks, on-screen text, and captions. Stronger = better copy, more expensive per run.',
  'Ohne Musik': 'No music',
  'nichts gewählt = das Ziel des Projekts': 'nothing selected = the project’s goal',
  'der letzte Satz der Caption — was der Zuschauer tun soll. Leer = wie im Projekt.': 'the last sentence of the caption — what the viewer should do. Empty = as in the project.',
  'je Lauf neu — bei unabhängigen Themen meist Einzelposts': 'decided fresh each run — usually single posts for unrelated topics',
  'erzwingt einen Bogen, auch wenn die Themen erst mal nicht zusammenpassen': 'forces an arc, even if the topics don’t seem to fit together at first',
  'Was diesen Agenten von den anderen unterscheidet. Briefing und Ideen des Projekts gelten ohnehin für alle.': 'What sets this agent apart from the others. The project’s briefing and ideas already apply to all of them.',
  'z. B. Nur Themen für Einsteiger. Stell in jedem Post eine Frage ans Publikum.': 'e.g. Only topics for beginners. Ask the audience a question in every post.',
  'Farbe des Agenten wählen': 'Choose the agent’s color',
  'Farbe wählen': 'Choose a color',
  'braucht kein Videomodell, einer mit „Nur Videos': 'doesn’t need a video model, one with "Videos only"',
  'Keine Bilddatei dabei.': 'No image file included.',
  'Projekt öffnen': 'Open project',
  'Projekt ändern': 'Change project',
  'Kosten schätzen': 'Estimate cost',
  'Post ändern': 'Change post',
  'Entwürfe freigeben': 'Approve drafts',
  'Entwürfe verwerfen': 'Discard drafts',
  'Zugänge prüfen': 'Check connections',
  'Was ist gerade offen?': 'What’s currently open?',
  'Bau mir 3 Posts für nächste Woche': 'Build me 3 posts for next week',
  'Was kostet ein Video-Post?': 'What does a video post cost?',
  'Schreib die Caption vom letzten Entwurf frecher': 'Make the caption of the last draft cheekier',
  'Der Assistent ist nicht erreichbar.': 'The assistant is unreachable.',
  'Veröffentlichen erlauben': 'Allow publishing',
  'Der Assistent darf Posts live schalten.': 'The assistant may publish posts live.',
  'Der Assistent legt nur Entwürfe an.': 'The assistant only creates drafts.',
  'Was die AI über die App weiß': 'What the AI knows about the app',
  'Was die App ist. Ohne das muss die AI alles erfinden.': 'What the app is. Without this, the AI has to invent everything.',
  'Themen als Ausgangspunkt. Die AI darf davon abweichen.': 'Topics as a starting point. The AI is free to deviate from them.',
  'Markdown oder Text. Alles, was die AI über die App wissen soll und nicht ins Briefing passt.': 'Markdown or text. Anything the AI should know about the app that doesn’t fit in the briefing.',
  'Sag in einem Satz, was passieren soll, der Assistent macht es': 'Say in one sentence what should happen, the assistant does it',
  'Betreiberkonto ohne Guthabengrenze — öffnet Tarife und Pakete, wie Kunden sie sehen': 'Operator account with no credit limit — opens plans and packages the way customers see them',
  'wird gebaut…': 'being built…',
  'Keine Caption': 'No caption',
  'Von diesem Agenten gebaut': 'Built by this agent',
  'Veröffentlichung fehlgeschlagen': 'Publishing failed',
  'Keine Textzeile in dieser Szene.': 'No text line in this scene.',
  'Erzeugt mit': 'Generated with',
  'von dir angelegt': 'created by you',
  'Im Story Builder öffnen': 'Open in the Story Builder',
  'Sofort veröffentlichen': 'Publish now',
  'Es kann gerade nichts gebaut werden': 'Nothing can be built right now',
  'Zu den Connectoren': 'Go to connectors',
  'Freigegeben, wird jetzt gerendert.': 'Approved, now rendering.',
  'Entwürfe werden geladen…': 'Loading drafts…',
  'Entwürfe aus allen Projekten, gerade ist nichts offen': 'Drafts from all projects — nothing open right now',
  'alle Projekte': 'all projects',
  'Alle freigeben': 'Approve all',
  'Sobald der Agent neue Entwürfe gebaut hat, landen sie hier. Du kannst auch selbst einen Lauf starten.': 'Once the agent has built new drafts, they’ll show up here. You can also start a run yourself.',
  'Projekt öffnen →': 'Open project →',
  'Ja, alle freigeben': 'Yes, approve all',
  'Projekte werden geladen…': 'Loading projects…',
  'geplant, noch nicht raus': 'scheduled, not out yet',
  'warten auf dich': 'waiting on you',
  'über alle Projekte': 'across all projects',
  'Willkommen in der AI Feed Factory': 'Welcome to AI Feed Factory',
  'Bibliothek für alle Projekte': 'Library for all projects',
  'Wird vorbereitet': 'Being prepared',
  'Die AI liest dein Briefing': 'The AI is reading your briefing',
  'Fehlt noch': 'Still missing',
  'Kein Account verbunden': 'No account connected',
  'Wartet auf dich': 'Waiting on you',
  'aus, Läufe startest du selbst': 'off, you start runs yourself',
  'Follower über alle Konten': 'Followers across all accounts',
  'Views der letzten 30 Tage': 'Views in the last 30 days',
  'veröffentlichte Posts': 'published posts',
  'Kein Untertitel': 'No subtitle',
  'kein Konto verbunden': 'no account connected',
  'Öffnen →': 'Open →',
  'denkt sich die Geschichte aus und schreibt die Texte': 'comes up with the story and writes the copy',
  'malt die Szenen': 'paints the scenes',
  'optional, für gesprochene Posts': 'optional, for narrated posts',
  'Anthropic, Google oder OpenAI': 'Anthropic, Google, or OpenAI',
  'nur für echte Clips nötig': 'only needed for real clips',
  'gilt für alle Projekte: Developer-Apps, Generatoren, Diagnose': 'applies to all projects: developer apps, generators, diagnostics',
  'gilt für alle Projekte: welche Generatoren deine Agenten nutzen dürfen': 'applies to all projects: which generators your agents are allowed to use',
  'Was aktiv ist, kannst du in deinen Agenten wählen. Ein Anbieter wird aktiv, sobald sein Key hier steht und funktioniert.': 'What’s active you can choose in your agents. A provider becomes active as soon as its key is set here and works.',
  'Was aktiv ist, können deine Agenten nutzen.': 'Your agents can use whatever is active.',
  'Normales Kundenkonto: zahlt in Credits und nutzt die Zugänge der Plattform.': 'Regular customer account: pays in credits and uses the platform’s own access.',
  'Noch nicht startklar': 'Not ready yet',
  'Planung, Texte und Bilder sind abgedeckt. Deine Agenten können loslegen.': 'Planning, copy, and images are covered. Your agents are ready to go.',
  'Preis pro Einheit direkt beim Anbieter — genau das landet auf deiner Rechnung dort.': 'Price per unit directly from the provider — exactly what ends up on your bill there.',
  'Einkaufspreise pro Einheit. Aus diesen Zahlen berechnen sich die Credits.': 'Purchase prices per unit. Credits are calculated from these numbers.',
  'Medien-Bucket. Die öffentliche Domain muss bei TikTok für PULL_FROM_URL verifiziert sein.': 'Media bucket. The public domain must be verified with TikTok for PULL_FROM_URL.',
  'nicht gefunden': 'not found',
  'Plant Inhalte, schreibt Texte und steuert den Assistenten.': 'Plans content, writes copy, and powers the assistant.',
  'aktiv · Audit läuft noch': 'active · audit still in progress',
  'aktiv · Direct Post möglich': 'active · direct posting available',
  'nicht konfiguriert': 'not configured',
  'Client Key und Secret liegen als Umgebungsvariablen im Backend.': 'Client key and secret are set as environment variables on the backend.',
  'Pro Projekt ein Business- oder Creator-Konto nötig.': 'One Business or Creator account needed per project.',
  'Stripe nicht konfiguriert': 'Stripe not configured',
  'Ohne Stripe lassen sich keine Credits kaufen.': 'Without Stripe, credits can’t be purchased.',
  'In der Server-Umgebung ist kein einziger Anbieter-Key hinterlegt.': 'No provider key at all is set in the server environment.',
  'Was die Anbieter kosten': 'What the providers cost',
  'Preis geschätzt': 'Price estimated',
  'Sobald der echte Preis bekannt ist, gehört er in': 'Once the real price is known, it belongs in',
  'Zahlung eingegangen, die Credits werden gerade gutgeschrieben.': 'Payment received, credits are being added now.',
  'Kauf nicht möglich': 'Purchase not possible',
  'Portal nicht verfügbar': 'Portal not available',
  'Dieses Konto läuft': 'This account runs',
  'ohne Guthabengrenze': 'with no credit limit',
  '— es gibt nichts zu kaufen. Was hier steht, ist reine Buchführung: was verbraucht wurde und wofür.': '— there’s nothing to buy. What’s shown here is pure bookkeeping: what was spent and on what.',
  'Credits sind die Währung für alles, was Rechenzeit kostet: Bild, Clip, Text, Rendern, Veröffentlichen. Der': 'Credits are the currency for everything that costs compute: images, clips, copy, rendering, publishing. Your',
  'schreibt dir monatlich welche gut und bestimmt, wie viele Projekte laufen dürfen.': 'credits you monthly and determines how many projects can run.',
  'lädst du einmalig nach, sie laufen nie ab.': 'you top up once, and they never expire.',
  'ohne Grenze': 'no limit',
  'kein Tageslimit': 'no daily limit',
  'Reicht noch': 'Still enough',
  'Guthaben wird knapp': 'Running low on credits',
  'Läuft es leer, pausiert der Autopilot, bereits geplante Posts werden dann nicht mehr gerendert und gehen nicht raus.': 'Once it runs out, the autopilot pauses — already scheduled posts stop rendering and won’t go out.',
  'läuft nicht ab': 'never expires',
  'Was ein Post ungefähr kostet': 'What a post roughly costs',
  'Zwei Bremsen: eine gegen leeres Guthaben, eine gegen zu hohe Ausgaben.': 'Two brakes: one against running out of credits, one against overspending.',
  'Tageslimit für die ganze Organisation': 'Daily limit for the whole organization',
  'Harte Obergrenze über alle Projekte. Leer lassen für kein Limit.': 'A hard cap across all projects. Leave empty for no limit.',
  'Was verbraucht wurde gegenüber dem, was wir den Anbietern zahlen.': 'What was spent versus what we pay the providers.',
  'Wohin das Geld fließt': 'Where the money goes',
  'Noch keine Buchungen.': 'No entries yet.',
  'Was jeder eingebracht hat, gegen das, was er uns gekostet hat. Nur für dich sichtbar.': 'What each customer brought in, against what they cost us. Visible only to you.',
  'noch nichts gezahlt': 'nothing paid yet',
  'Noch keine Kunden.': 'No customers yet.',
  'Bitte eine Zahl groesser als null.': 'Please enter a number greater than zero.',
  'Erklärung ausblenden': 'Hide explanation',
  'Was ist das?': 'What is this?',
  'Dieses Projekt braucht mehr Platz': 'This project needs more room',
  'Mehr Accounts pro Projekt': 'More accounts per project',
  'TikTok, Instagram und Facebook zählen einzeln. Wer mehrere Kanäle aus einem Projekt bespielt, braucht entsprechend Plätze.': 'TikTok, Instagram and Facebook count separately. Running several channels from one project needs enough slots for it.',
  'Ein zweiter Agent': 'A second agent',
  'Das Guthaben reicht nicht': 'Not enough credits',
  'Der nächste Schritt, und was danach kommt': 'The next step, and what comes after',
  'Alle Preise ansehen': 'View all prices',
  'Mehrere Agenten je Projekt, zum Vergleichen': 'Multiple agents per project, to compare',
  'Ungenutzte Credits wandern in den Folgemonat': 'Unused credits roll over to the next month',
  'Mehr als zwei Konten je Projekt': 'More than two accounts per project',
  'Wechsel nicht möglich': 'Switch not possible',
  'Tarife werden geladen…': 'Loading plans…',
  'Zahlungen sind gerade nicht eingerichtet. Im Backend fehlt': 'Payments aren’t set up right now. The backend is missing',
  '. Die Tarife stehen hier trotzdem, damit du siehst, was jeweils drin ist.': '. The plans are still shown here so you can see what each one includes.',
  'Dein Tarif': 'Your plan',
  'Duplizieren ging nicht': 'Duplication failed',
  'je ein Rezept: Stil, Szenen, Ziel, Modelle': 'one recipe each: style, scenes, goal, models',
  'überlegt sich Ideen…': 'coming up with ideas…',
  'Noch kein Agent': 'No agent yet',
  'Leg einen an und probier ihn aus. Wenn das Ergebnis passt, hängst du ihn im Autopilot in den Fahrplan.': 'Create one and try it out. If you like the result, add it to the schedule in Autopilot.',
  'Noch ein Agent?': 'Another agent?',
  'Ein zweites Rezept daneben stellen — anderer Stil, anderes Ziel — und sehen, welches mehr Reichweite je Credit bringt.': 'Put a second recipe next to it — different style, different goal — and see which one gets more reach per credit.',
  'Diesen Agenten gibt es nicht mehr': 'This agent no longer exists',
  'Vielleicht wurde er gelöscht.': 'It may have been deleted.',
  'AI wählt': 'AI picks',
  'davon draußen': 'already live',
  'über alle Posts': 'across all posts',
  'die Zahl, die zählt': 'the number that matters',
  'Noch nichts gebaut.': 'Nothing built yet.',
  'Noch nichts gebaut. „Laufen lassen" unten probiert das Rezept aus.': 'Nothing built yet. "Run now" below tries out the recipe.',
  'Eigener Prompt, noch leer': 'Custom prompt, still empty',
  'Kein fester Look, die AI wählt je Post.': 'No fixed look, the AI picks per post.',
  'aus den Projekteinstellungen': 'from the project settings',
  'Dieser Agent läuft': 'This agent runs',
  'Ein Clip aus allen Szenen, mit Musik': 'A clip made from all scenes, with music',
  'Bildserie zum Durchwischen, TikTok legt die Musik selbst drauf': 'An image carousel to swipe through, TikTok adds the music itself',
  'Karussell zum Durchwischen, 4:5 zugeschnitten': 'A carousel to swipe through, cropped to 4:5',
  'Ein Clip, verschwindet nach 24 Stunden': 'A clip, disappears after 24 hours',
  'Anlegen ging nicht': 'Creation failed',
  'Szenen, Textzeilen, Bilder und Musik eines einzelnen Posts': 'The scenes, text lines, images, and music of a single post',
  'Kein Entwurf da': 'No draft here',
  'Leg selbst eine an und bau sie Szene für Szene, oder lass den Agenten einen ganzen Schwung planen.': 'Create one yourself and build it scene by scene, or have the agent plan a whole batch.',
  'Ohne Titel': 'Untitled',
  'ohne Musik': 'no music',
  'Szenen und Texte per Hand, alles landet genauso im fertigen Video. Änderungen wirken erst nach dem Speichern.': 'Scenes and text by hand — everything lands exactly like this in the finished video. Changes take effect only after saving.',
  'Änderungen speichern': 'Save changes',
  'Szene gelöscht.': 'Scene deleted.',
  'Szene löschen': 'Delete scene',
  'jede Zeile wird ins Bild gerendert, in der Vorschau ziehbar': 'each line is rendered into the image, and can be dragged in the preview',
  'Keine Textzeile in dieser Szene. Die meisten schauen ohne Ton, ohne Text im Bild geht die Aussage verloren.': 'No text line in this scene. Most people watch without sound — without on-screen text, the message gets lost.',
  'Zeile löschen': 'Delete line',
  'Mit farbigem Balken hervorheben (Akzentfarbe, höchstens eine Zeile pro Beitrag)': 'Highlight with a colored bar (accent color, at most one line per post)',
  'Caption, oder von der AI schreiben lassen…': 'Caption, or have the AI write one…',
  'Freigegeben, wird gerendert.': 'Approved, now rendering.',
  'Rendern angestoßen.': 'Rendering started.',
  'Szene hinzugefügt.': 'Scene added.',
  'Szene hinzufügen': 'Add scene',
  'Beschreib auf Englisch, was zu sehen sein soll, nicht den Text, der draufkommt.': 'Describe in English what should be visible, not the text that goes on top.',
  'Schätzung nicht möglich': 'Estimate not possible',
  'Autopilot läuft.': 'Autopilot is running.',
  'Lauf gestartet, schau dem Agenten zu.': 'Run started, watch the agent work.',
  'alle Plattformen': 'all platforms',
  'Was der Agent als Nächstes plant': 'What the agent plans next',
  'klick für Details, du kannst alles noch ändern': 'click for details, you can still change everything',
  'Nichts geplant. Starte einen Lauf oder schalte den Autopiloten ein.': 'Nothing planned. Start a run or turn on the autopilot.',
  'Änderung noch nicht übernommen, im Autopilot bestätigen.': 'Change not applied yet, confirm it in Autopilot.',
  'Noch keiner verbunden.': 'None connected yet.',
  'Noch nichts veröffentlicht.': 'Nothing published yet.',
  'anderen wählen': 'choose another',
  'Geschätzte Kosten': 'Estimated cost',
  ', das reicht nicht. Bitte erst aufladen.': ', that’s not enough. Please top up first.',
  'Großzügig gerechnet, der Rest wird nach dem Lauf wieder frei.': 'Calculated generously — the rest is freed up again after the run.',
  'Kosten werden geschätzt…': 'Estimating cost…',
  'jeder Post vom Entwurf bis zum Sendetermin': 'every post from draft to send time',
  'Die Queue ist leer': 'The queue is empty',
  'Starte einen Planungslauf, dann füllt der Agent sie mit fertigen Entwürfen.': 'Start a planning run, and the agent fills it with finished drafts.',
  'Wechsle den Filter, um andere Posts zu sehen.': 'Switch the filter to see other posts.',
  'werden gerendert und zur geplanten Zeit veröffentlicht. Das Rendern kostet Credits.': 'get rendered and published at the scheduled time. Rendering costs credits.',
  'Übernommen, gilt ab dem nächsten Lauf.': 'Applied, takes effect from the next run.',
  'Bild wählen': 'Choose image',
  'Steht überall neben dem Bild. Nur für dich, nicht im Post.': 'Shown everywhere next to the image. Only for you, not in the post.',
  'Ein Halbsatz, was die App ist. Die AI hat einen vorgeschlagen.': 'A short phrase for what the app is. The AI suggested one.',
  'z. B. Wahrsage-App für iOS und Android': 'e.g. Fortune-telling app for iOS and Android',
  'Die AI leitet gerade Untertitel, Tonfall und erste Ideen aus deinem Briefing ab.': 'The AI is deriving the subtitle, tone of voice, and first ideas from your briefing.',
  'Vorgabe für alle Agenten': 'Default for all agents',
  'Was hier steht, gilt für jeden Agenten dieses Projekts, der nichts Eigenes sagt.': 'What’s set here applies to every agent in this project that doesn’t override it.',
  'Beim einzelnen Agenten lässt sich alles davon übersteuern — dort stellst du ein, was': 'Any of it can be overridden on the individual agent — that’s where you set what',
  'dieser eine': 'this one',
  'erreichen soll und wie er klingt.': 'should achieve and how it sounds.',
  'Ziel, wenn ein Agent keins nennt': 'Goal, when an agent doesn’t name one',
  'das erste gewinnt': 'the first one wins',
  'zuerst gewählte': 'first chosen',
  'den Bau, die anderen färben nur ein. Sonst würde bei jedem Post etwas anderes gewinnen.': 'the build, the others only add color. Otherwise a different one would win on every post.',
  'Was sollen die Leute tun?': 'What should people do?',
  'Vorlage wählen …': 'Choose a template …',
  '…oder eigener Satz, der unter jedem Post steht': '…or your own sentence that appears under every post',
  'In welcher Sprache wird gepostet?': 'What language are posts written in?',
  'Wie sollen die Bilder aussehen?': 'What should the images look like?',
  'Projekt löschen': 'Delete project',
  'wer ist das jetzt': 'who’s this again',
  'Noch kein Medium': 'No media yet',

  /* --- Batch 1: Onboarding & Auth --- */
  'Deine App bekommt einen Feed.': 'Your app gets a feed.',
  'Du schaust nur zu.': 'You just watch.',
  'Beschreib einmal, was deine App macht. Die AI erfindet die Hooks, generiert Bilder und Clips, schreibt Captions, legt Musik drunter, rendert fertige Videos und postet sie zur richtigen Zeit auf TikTok, Instagram und Facebook.': 'Describe what your app does, once. The AI comes up with the hooks, generates images and clips, writes captions, adds music, renders finished videos, and posts them at just the right time on TikTok, Instagram and Facebook.',
  'Ein Briefing genügt': 'One briefing is enough',
  'Aus einer Beschreibung wird Tonalität, Bildstil und ein Ideen-Vorrat.': 'One description becomes a tone of voice, an image style, and a stockpile of ideas.',
  'Jedes Projekt eigene Accounts': 'Each project has its own accounts',
  'TikTok, Instagram und Facebook werden pro App verbunden, sauber getrennt.': 'TikTok, Instagram and Facebook are connected per app, kept cleanly separate.',
  'Nichts geht ungefragt live': 'Nothing goes live without asking',
  'Standardmäßig landet alles als Entwurf. Vollautomatik nur, wenn du sie einschaltest.': 'By default, everything lands as a draft. Full automation only if you turn it on.',
  'Du siehst, was es kostet': 'You see what it costs',
  'Vor jedem Lauf steht die Rechnung. Kein Abo-Blindflug.': 'The bill is shown before every run. No subscription flying blind.',
  'oder mit E-Mail': 'or continue with email',
  'Dein Name': 'Your name',
  'Max Muster': 'John Doe',
  'Firma oder Team': 'Company or team',
  '(optional)': '(optional)',
  'z. B. Muster Studio': 'e.g. Acme Studio',
  'du@beispiel.de': 'you@example.com',
  'mindestens 10 Zeichen': 'at least 10 characters',
  'Mit Google starten': 'Sign up with Google',
  'Mit dem Anlegen stimmst du den': 'By creating an account, you agree to the',
  'und der': 'and the',
  'Datenschutzerklärung': 'Privacy Policy',
  'zu.': '.',
  'Du bekommst Startguthaben, genug für die ersten Posts, ohne Zahlungsdaten.': 'You get starter credits, enough for your first posts, no payment details required.',
  '{name}, was die App ist:': '{name}, what the app is:',
  'Zielgruppe:': 'Target audience:',
  'Diese Funktionen sollen beworben werden:': 'These features should be promoted:',
  '„{name}" angelegt. Jetzt Accounts verbinden.': '"{name}" created. Now connect your accounts.',
  'Anlegen fehlgeschlagen.': 'Creation failed.',
  'Neues Projekt': 'New project',
  'Ein Projekt = eine App mit eigenem Feed, eigenen Accounts und eigenem Zeitplan.': 'One project = one app with its own feed, accounts, and schedule.',
  'Projekt anlegen': 'Create project',
  'App': 'App',
  'Briefing': 'Briefing',
  'Wie heißt die App?': 'What is the app called?',
  'Wo gibt es sie?': 'Where can people find it?',
  ', beides optional, eins reicht, keins geht auch': ', both optional, one is enough, none works too',
  'Die Links landen im Briefing. Die AI liest daraus den Namen im Store, die Kategorie und passt den Call-to-Action an die Plattform an: „im App Store" klingt anders als „bei Google Play".': 'The links go into the briefing. The AI reads the store name and category from them and adapts the call to action to the platform: "on the App Store" sounds different from "on Google Play".',
  'Was hier gleich entsteht': 'What happens next',
  'Dieses Projekt produziert': 'This project produces',
  'vertikale Videos für TikTok und Instagram Reels': 'vertical videos for TikTok and Instagram Reels',
  ', automatisch, nach einem Zeitplan, den du setzt. Die AI erfindet die Hooks, generiert Bilder oder Clips, schreibt Caption und Hashtags, legt Musik drunter und rendert ein fertiges MP4. Du gibst frei, dann geht es raus.': ', automatically, on a schedule you set. The AI comes up with the hooks, generates images or clips, writes the caption and hashtags, adds music, and renders a finished MP4. You approve it, then it goes out.',
  'Schnell': 'Quick',
  'Du beschreibst nur die App. Zielgruppe, Funktionsliste und acht Beitragsideen leitet die AI daraus ab.': 'You just describe the app. The AI derives the audience, feature list, and eight post ideas from it.',
  'Genau': 'Precise',
  'Du legst Zielgruppe und Funktionen selbst fest. Dauert ein paar Minuten und trifft deutlich besser.': 'You set the audience and features yourself. Takes a few minutes and hits the mark much better.',
  '{answered} von {total} beantwortet': '{answered} of {total} answered',
  'Was macht die App genau?': 'What exactly does the app do?',
  'Das Wichtigste im ganzen Ablauf und die einzige Stelle, an der sich Ausführlichkeit wirklich auszahlt. Alles, was hier steht, muss die AI später nicht erfinden: Funktionen, Fachbegriffe, Besonderheiten, für wen sie gedacht ist, und auch, was die App ausdrücklich nicht ist. Schreib so viel du willst.': 'The most important part of the whole process, and the one place where detail really pays off. Anything written here is something the AI won\'t have to invent later: features, terminology, quirks, who it\'s for, and also what the app explicitly is not. Write as much as you want.',
  'Was kann man mit der App tun?\nFür welches Problem ist sie da?\nWas ist die wichtigste Funktion, und was macht sie anders als andere?\nWas passiert, wenn man sie zum ersten Mal öffnet?': 'What can you do with the app?\nWhat problem does it solve?\nWhat is the most important feature, and what makes it different from others?\nWhat happens when you open it for the first time?',
  'Für wen ist sie?': 'Who is it for?',
  'Alter, Situation, was diese Menschen gerade beschäftigt. Klick an, was passt, und ergänze es.': 'Age, situation, what\'s on these people\'s minds right now. Click what fits, and add to it.',
  'Wer nutzt die App, und in welcher Lage sind diese Menschen gerade?': 'Who uses the app, and what situation are these people in right now?',
  'Welche Funktionen sollen beworben werden?': 'Which features should be promoted?',
  'Drei bis fünf, eine pro Zeile. Klick an, was zutrifft, und ergänze das, was deine App besonders macht.': 'Three to five, one per line. Click what applies, and add what makes your app special.',
  'Eine Funktion pro Zeile': 'One feature per line',
  'Post-Ideen, die du im Kopf hast': 'Post ideas you already have in mind',
  'Optional. Eine pro Zeile. Der Agent arbeitet sie ab, bevor er selbst welche erfindet.': 'Optional. One per line. The agent works through these before inventing its own.',
  'Eine Idee pro Zeile': 'One idea per line',
  'Keine Lust zu tippen? Kopier den Auftrag, gib ihn einem Sprachmodell und zieh die Datei, die dabei herauskommt, unten wieder herein.': 'Don\'t feel like typing? Copy the prompt, give it to a language model, and drag the file it produces back in below.',
  'Auch die Ideen kannst du dir schreiben lassen. Der Auftrag enthält deine Beschreibung von oben.': 'You can have the ideas written for you too. The prompt includes your description from above.',
  'Auftrag kopieren': 'Copy prompt',
  'In der Zwischenablage': 'Copied to clipboard',
  'Kopiert einen fertigen Auftrag für ChatGPT, Claude oder ein anderes Sprachmodell': 'Copies a ready-made prompt for ChatGPT, Claude, or another language model',
  'Ideen als Datei einlesen': 'Import ideas from a file',
  'Beschreibung als Datei einlesen': 'Import description from a file',
  'Ziemlich lang. Das Briefing wird vor jedem einzelnen Beitrag mitgelesen. Sehr ausführliche Texte kosten also bei jedem Lauf etwas mehr Credits.': 'Pretty long. The briefing is read along with every single post. Very detailed text costs a bit more in credits on every run.',
  'Die AI ergänzt aus deiner Beschreibung Zielgruppe, Funktionsliste und acht Beitragsideen. Hast du ausführlich geschrieben, bleibt dein Text so stehen, wie er ist, gekürzt wird nichts. Ändern kannst du alles danach unter': 'The AI fills in the audience, feature list, and eight post ideas from your description. If you wrote in detail, your text stays exactly as it is — nothing gets shortened. You can change everything afterward under',
  'Was gleich passiert': 'What happens next',
  'Das Projekt entsteht sofort. Im Hintergrund verdichtet die AI dein Briefing und leitet erste Post-Ideen ab, das dauert noch etwas. Danach führt dich eine Checkliste durch den Rest: Accounts verbinden, Bild-Generator hinterlegen, ersten Agenten anlegen.': 'The project is created right away. In the background, the AI condenses your briefing and derives initial post ideas, which takes a little longer. After that, a checklist guides you through the rest: connect accounts, set up an image generator, create your first agent.',
  'Nichts geht live': 'Nothing goes live',
  ', bevor du es freigibst.': ', before you approve it.',
  'Das entstandene Briefing ansehen': 'View the resulting briefing',
  'Zeichen': 'characters',
  'Mehr passt nicht ins Feld': 'No more fits in this field',
  '„{name}" löschen': '"{name}" delete',
  'Letzte Warnung': 'Final warning',
  'Was mit gelöscht wird': 'What gets deleted with it',
  'Posts': 'posts',
  'davon {n} bereits veröffentlicht': 'of which {n} already published',
  'Videos': 'videos',
  'und': 'and',
  'Bilder': 'images',
  'Speicher': 'storage',
  'Bereits veröffentlichte Beiträge bleiben auf TikTok, Instagram und Facebook online. Die liegen dort, nicht hier. Löschen musst du sie in der jeweiligen App.': 'Already published posts stay online on TikTok, Instagram and Facebook. They live there, not here. You have to delete them in the respective app.',
  'Tippe': 'Type',
  '„{name}" werden endgültig entfernt.': '"{name}" will be permanently removed.',
  'Alle Inhalte von „{name}" werden endgültig entfernt.': 'All content from "{name}" will be permanently removed.',
  '{name} ist keine Textdatei. Erlaubt: {list}': '{name} is not a text file. Allowed: {list}',
  '{name} übernommen · {count} Zeichen.': '{name} imported · {count} characters.',
  'Loslassen': 'Drop it',
  ', die dir ein Sprachmodell über deine App geschrieben hat. Der Inhalt landet im Feld darunter und bleibt dort ganz normal bearbeitbar.': ', that a language model wrote about your app. The content lands in the field below and stays normally editable there.',
  '{n} Screenshot{s} hinzugefügt.': '{n} screenshot{s} added.',
  'Screenshot entfernt.': 'Screenshot removed.',
  'Damit manche Posts die App wirklich zeigen können: ein Handy in der Hand, die App offen auf dem Tisch. Ohne Vorlage denkt sich das Bildmodell eine Oberfläche aus, die deiner App nicht ähnlich sieht.': 'So some posts can actually show the app: a phone in hand, the app open on the table. Without a reference, the image model invents an interface that looks nothing like your app.',
  'Screenshot entfernen': 'Remove screenshot',
  'nie': 'never',
  'Ungefähr jeder {n}. Post zeigt die App, der Rest erzählt das Thema ohne sie.': 'Roughly every {n}th post shows the app, the rest tell the story without it.',
  'Dashboard': 'Dashboard',

  /* --- Batch 6: Autopilot & AgentRun --- */
  'Autopilot gespeichert.': 'Autopilot saved.',
  'Alle {n} Plätze der Woche sind vergeben.': 'All {n} slots for the week are taken.',
  'Eingeplant: +1 Post pro Woche.': 'Scheduled: +1 post per week.',
  'wann gepostet wird und von welchem Agenten': 'when it posts and which agent builds it',
  'Ein Projekt veröffentlicht höchstens': 'A project publishes at most',
  '{n} Posts am Tag': '{n} posts a day',
  'über alle Agenten zusammen. Das ist keine Geschmacksfrage: TikTok, Instagram und Facebook werten häufigeres Posten aus einem Konto als Spam und drosseln es. Die eingeplanten Agenten teilen sich deshalb dieselben Plätze — wer öfter drankommt, verdrängt einen anderen. Wie die Posts aussehen, steht im jeweiligen': 'across all agents combined. This isn’t a matter of taste: TikTok, Instagram and Facebook treat frequent posting from one account as spam and throttle it. Scheduled agents therefore share the same slots — one posting more often crowds out another. What the posts look like is defined in the respective',
  'gespeichert': 'saved',
  'Verbinde zuerst ein TikTok- oder Instagram-Konto — sonst hätten die Posts kein Ziel.': 'Connect a TikTok, Instagram or Facebook account first — otherwise the posts would have nowhere to go.',
  'Es passiert nichts von selbst. Einzelne Läufe startest du bei den Agenten.': 'Nothing happens on its own. You start individual runs from the agents.',
  'Posts gehen ohne Rückfrage live, sobald sie fertig sind.': 'Posts go live without confirmation as soon as they’re ready.',
  'Es entstehen Entwürfe, du gibst sie frei. Nichts geht ungefragt raus.': 'Drafts are created and you approve them. Nothing goes out without your say-so.',
  'Er versucht es alle 30 Minuten erneut. Sobald es behoben ist, läuft er von allein weiter — der angezeigte Termin gilt bis dahin nicht.': 'It retries every 30 minutes. Once the issue is resolved, it continues on its own — the displayed time doesn’t apply until then.',
  'Der Autopilot lässt sich erst einschalten, wenn TikTok, Instagram oder Facebook verbunden ist — sonst hätten die fertigen Posts kein Ziel. Das machst du unter': 'The autopilot can only be turned on once TikTok, Instagram or Facebook is connected — otherwise the finished posts would have nowhere to go. You can do that under',
  'Nichts ausgewählt heißt: überall hin, wo ein Konto verbunden ist. „Video" wird ein Clip aus allen Szenen, „Fotos" und „Feed" werden Bildserien, durch die der Zuschauer selbst wischt.': 'Nothing selected means: everywhere an account is connected. "Video" becomes a clip made from all scenes; "Photos" and "Feed" become image carousels the viewer swipes through.',
  'Dafür ist kein Konto verbunden': 'No account is connected for this',
  '✓ Mit Freigabe': '✓ With approval',
  '↻ Vollautomatik': '↻ Fully automatic',
  'Was der Fahrplan baut, geht direkt live. Läufe, die du bei den Agenten von Hand startest, bleiben trotzdem immer Entwürfe — sonst könntest du keinen Agenten ausprobieren, ohne ungeprüft zu veröffentlichen.': 'Whatever the schedule builds goes straight live. Runs you start manually from the agents still always stay drafts — otherwise you couldn’t try out an agent without publishing it unchecked.',
  'Alles landet als Entwurf in der Queue. Du siehst jeden Post, bevor er raus geht. Empfohlen für den Anfang.': 'Everything lands as a draft in the queue. You see every post before it goes out. Recommended to start with.',
  'Die nächsten sieben Tage': 'The next seven days',
  'Genau diese Termine arbeitet der Autopilot ab. Gebaut wird jeweils zwei Stunden vorher.': 'The autopilot works through exactly these times. Building starts two hours before each one.',
  'So sähe es aus, wenn der Autopilot liefe.': 'This is what it would look like if the autopilot were running.',
  'Wie TikTok-Posts rausgehen': 'How TikTok posts go out',
  'Gilt nur für TikTok. Instagram kennt keine Entwürfe, dort geht immer das fertige Video mit Musik aus unserer Bibliothek raus.': 'Applies to TikTok only. Instagram has no drafts — the finished video always goes out with music from our library.',
  '↗ Direkt posten': '↗ Post directly',
  '✎ Als Entwurf, Musik selbst': '✎ As a draft, add your own music',
  'Jedes Video landet als privater Entwurf in deiner TikTok-App. Dort legst du Musik aus dem echten TikTok-Katalog drauf und postest selbst. Aufwendiger, aber Originalton aus dem Katalog trägt spürbar weiter als eingerenderte Musik.': 'Every video lands as a private draft in your TikTok app. There you add music from the real TikTok catalog and post it yourself. More effort, but original catalog audio noticeably outperforms rendered-in music.',
  'Fertige Videos gehen direkt live, mit Musik aus unserer Bibliothek. Solange das TikTok-Audit noch läuft, landen sie trotzdem als Entwurf — TikTok lässt Direktposten vorher nicht zu.': 'Finished videos go straight live, with music from our library. While the TikTok audit is still in progress, they land as a draft anyway — TikTok doesn’t allow direct posting before that.',
  'Ausgabengrenze': 'Spending limit',
  'Eine Obergrenze in Credits, nicht in Posts — wie viele Posts es sind, sagt der Fahrplan. Ist sie erreicht, pausiert der Autopilot bis zum nächsten Tag, auch bei Vollautomatik.': 'A cap in credits, not in posts — the schedule decides how many posts that is. Once it’s reached, the autopilot pauses until the next day, even in fully automatic mode.',
  'Credits pro Tag': 'Credits per day',
  'Was der Agent gelernt hat': 'What the agent has learned',
  'Abbruch angefordert.': 'Abort requested.',
  'Der letzte Lauf hat noch {n} offene Posts. Ein neuer Lauf plant von vorn und kostet erneut. Trotzdem neu starten?': 'The last run still has {n} open posts. A new run plans from scratch and costs again. Start a new one anyway?',
  'Lauf gestartet.': 'Run started.',
  'Start fehlgeschlagen': 'Start failed',
  'live für {name}': 'live for {name}',
  '{n} Agenten arbeiten gerade:': '{n} agents are working right now:',
  'Schreibt die Texte, Post {a} von {b}': 'Writing the copy, post {a} of {b}',
  'Baut gerade Post {a} von {b}': 'Building post {a} of {b} now',
  'Lauf abgeschlossen': 'Run completed',
  'Lauf fehlgeschlagen': 'Run failed',
  'Lauf abgebrochen': 'Run aborted',
  '{a} von {b} Posts noch offen. „Weitermachen" holt sie nach, ohne neu zu planen.': '{a} of {b} posts still open. "Resume" catches them up without replanning.',
  'Nächster Planungslauf {when}': 'Next planning run {when}',
  'Abbruch angefordert…': 'Abort requested…',
  'Lauf abbrechen': 'Abort run',
  'Weitermachen ({n})': 'Resume ({n})',
  'Verwerfen, neu planen': 'Discard, plan again',
  'Lauf starten': 'Start run',
  'Lauf-Fortschritt': 'Run progress',
  'Ersten Lauf starten': 'Start first run',
  'folgen': 'follow',
  'Post {a} von {b}': 'Post {a} of {b}',
  'Video-Story': 'Video Story',
  'Bild-Story': 'Image Story',
  'fertig': 'done',
  'läuft': 'running',
  'wartet': 'waiting',
  'Szene {n}': 'Scene {n}',
  'Szene {a} von {b}': 'Scene {a} of {b}',
  'SERIE': 'SERIES',
  'Dieser Lauf': 'This run',
  'Verbraucht': 'Spent',
  'Reserviert': 'Reserved',
  '{n} Credits': '{n} credits',
  'Ideen-Backlog': 'Idea backlog',
  'gestartet {ago}': 'started {ago}',
  'nur dieses Agenten': 'this agent only',
  'per Assistent': 'per assistant',
  'manuell': 'manual',
  ' · „{title}"': ' · "{title}"',
  'Steht. Je genauer es ist, desto besser wird jeder Post.': 'Done. The more precise it is, the better every post turns out.',
  'Erst {n} Zeichen. Unter 220 hat die AI zu wenig, um wirklich gute Hooks zu finden. Ergänze Zielgruppe, Features und den Ton.': 'Only {n} characters so far. Below 220 the AI has too little to work with to find really good hooks. Add your target audience, features, and tone.',
  'Ohne Briefing erfindet die AI ins Blaue. Das ist der wichtigste Schritt überhaupt.': 'Without a briefing, the AI is just guessing. This is the single most important step.',
  'Briefing bearbeiten': 'Edit briefing',
  'Kanäle verbinden (TikTok, Instagram, Facebook)': 'Connect channels (TikTok, Instagram, Facebook)',
  '{list} verbunden.': '{list} connected.',
  'Ohne verbundenen Account kann nichts veröffentlicht werden. Planen und Rendern geht trotzdem.': 'Without a connected account nothing can be published. Planning and rendering still work.',
  'Accounts verbinden': 'Connect accounts',
  'Bild-Generator hinterlegen': 'Add an image generator',
  'Ein Generator ist eingerichtet. Es entstehen echte Bilder.': 'A generator is set up. Real images are created.',
  'Ohne API-Key entstehen nur graue Platzhalter. Ein Replicate-Key genügt und kostet unter einem Cent pro Bild. Gilt für alle deine Projekte.': 'Without an API key you only get gray placeholders. A Replicate key is enough and costs under a cent per image. Applies to all your projects.',
  'Zu den Kontoeinstellungen': 'Go to account settings',
  'Genug Guthaben': 'Enough credits',
  'Reicht für die nächsten Läufe.': 'Enough for the next runs.',
  'Zu wenig Credits für einen Planungslauf. Der Agent startet erst gar nicht, statt mittendrin abzubrechen.': 'Not enough credits for a planning run. The agent won’t even start, rather than aborting midway.',
  'Guthaben ansehen': 'View credits',
  'Zeitplan festlegen': 'Set the schedule',
  '{label} in {windows} Uhr.': '{label} at {windows}.',
  'Kein Zeitfenster aktiv. Dann kann nichts eingeplant werden.': 'No time window active. Then nothing can be scheduled.',
  'Zeitplan öffnen': 'Open schedule',
  'Ersten Agenten anlegen': 'Create your first agent',
  'Es gibt ein Rezept, nach dem gebaut wird.': 'There’s a recipe in place to build from.',
  'Ein Agent sagt, WIE ein Post aussieht: Stil, Szenen, Ziel, Modelle. Ohne ihn baut niemand etwas.': 'An agent defines HOW a post looks: style, scenes, goal, models. Without one, nothing gets built.',
  'Agent anlegen': 'Create agent',
  'Es liegen bereits Posts vor.': 'Posts already exist.',
  'Der Agent baut die ersten Entwürfe. Nichts davon geht live, bevor du es freigibst.': 'The agent builds the first drafts. None of it goes live before you approve it.',
  'Autopilot einschalten': 'Turn on autopilot',
  'Vollautomatik. Posts gehen ohne Rückfrage live.': 'Fully automatic. Posts go live without confirmation.',
  'Läuft mit Freigabe: Der Agent baut, du entscheidest.': 'Runs with approval: the agent builds, you decide.',
  'Optional. Danach legt der Agent von selbst nach, sobald die Queue leer läuft.': 'Optional. After that the agent keeps building on its own once the queue runs dry.',
  'Autopilot einstellen': 'Set up autopilot',
  'Alles eingerichtet': 'All set up',
  'Schritte zeigen': 'Show steps',
  'Ausblenden': 'Hide',
  'Projekt einrichten': 'Set up project',
  '{a} von {b}': '{a} of {b}',
  'Als Nächstes:': 'Next up:',
  'Das Projekt läuft vollständig. Die Liste kannst du ausblenden.': 'The project is running fully. You can hide this list.',
  'Einklappen': 'Collapse',
  'optional': 'optional',

  /* --- Batch 7: Connections & socials --- */
  'Ein TikTok-Konto, das nur für dieses Projekt gedacht ist.': 'A TikTok account used only for this project.',
  'Du klickst auf „TikTok verbinden".': 'You click "Connect TikTok."',
  'Es öffnet sich ein Fenster von TikTok, nicht von uns.': 'A window from TikTok opens — not from us.',
  'Du meldest dich dort an und erlaubst den Zugriff.': 'You sign in there and grant access.',
  'Das Fenster schließt sich, der Account steht hier.': 'The window closes, and the account appears here.',
  'Ein Instagram Business- oder Creator-Konto, verknüpft mit einer Facebook-Seite.': 'An Instagram Business or Creator account linked to a Facebook Page.',
  'Du klickst auf „Instagram verbinden".': 'You click "Connect Instagram."',
  'Facebook fragt, welche Seite du freigeben willst.': 'Facebook asks which Page you want to grant access to.',
  'Wir suchen das Instagram-Konto hinter der Seite.': 'We look up the Instagram account behind that Page.',
  'Fertig, der Account steht hier.': 'Done — the account appears here.',
  'Verbunden.': 'Connected.',
  'Fehlgeschlagen.': 'Failed.',
  'Account verbunden.': 'Account connected.',
  'Verbindung fehlgeschlagen.': 'Connection failed.',
  'Verbindungen werden geprüft…': 'Checking connections…',
  'Die Plattform-App fehlt noch, sie wird in den Einstellungen hinterlegt.': 'The platform app hasn’t been set up yet — it’s configured in Settings.',
  'Diese Plattform ist noch nicht freigeschaltet. Melde dich beim Betreiber.': 'This platform isn’t enabled yet. Contact the operator.',
  'Verbindung nicht möglich': 'Couldn’t connect',
  '{name} wirklich trennen? Geplante Posts können dann nicht veröffentlicht werden.': 'Really disconnect {name}? Scheduled posts will no longer be able to publish.',
  'Getrennt.': 'Disconnected.',
  'Kennzahlen werden geholt, dauert einen Moment.': 'Fetching stats, this takes a moment.',
  'die Konten, auf denen {name} veröffentlicht': 'the accounts {name} publishes to',
  'noch nicht verbunden': 'not connected yet',
  'Verbunden': 'Connected',
  'Bereit': 'Ready',
  'App fehlt': 'App missing',
  'Follower': 'Followers',
  'Views 30 T.': 'Views 30d',
  'Likes': 'Likes',
  'Nullen sind normal: Im Sandbox-Betrieb liefert die Plattform noch keine echten Zahlen.': 'Zeros are normal here: in sandbox mode, the platform doesn’t return real numbers yet.',
  'Verbindung gültig': 'Connection valid',
  'abgelaufen': 'expired',
  'unbegrenzt': 'unlimited',
  'läuft heute ab': 'expires today',
  'noch {n} Tage': '{n} days left',
  'App-Audit läuft noch': 'App audit still in progress',
  'TikTok erlaubt bis zur Freigabe nur private Beiträge. Wir legen Posts deshalb als Entwurf in deine TikTok-Inbox, du bekommst eine Benachrichtigung und postest mit einem Tipp. Nach bestandenem Audit den Schalter unten in den Plattform-Apps umlegen.': 'Until it’s approved, TikTok only allows private posts. So we drop posts into your TikTok inbox as drafts — you’ll get a notification and can publish with one tap. Once the audit passes, flip the switch below in Platform Apps.',
  'Zahlen holen': 'Fetch stats',
  'Neu verbinden': 'Reconnect',
  'Trennen': 'Disconnect',
  'Zuletzt aktualisiert': 'Last updated',
  'Was passiert, wenn du klickst': 'What happens when you click',
  'Voraussetzung:': 'Requirement:',
  '{name} verbinden': 'Connect {name}',
  'Der Login läuft direkt bei {name}. Wir sehen keine Zugangsdaten, hier landet nur ein verschlüsseltes Token.': 'Sign-in happens directly with {name}. We never see your credentials — only an encrypted token lands here.',
  '{name} ist noch nicht freigeschaltet': '{name} isn’t enabled yet',
  'Die {name}-App der Installation fehlt noch. Sie gilt für alle Projekte und wird einmal in den Einstellungen hinterlegt.': 'The installation’s {name} app hasn’t been set up yet. It applies to all projects and only needs to be configured once, in Settings.',
  '{name} steht in dieser Installation noch nicht bereit. Der Betreiber schaltet es frei.': '{name} isn’t available in this installation yet. The operator needs to enable it.',
  'Zu den Einstellungen': 'Go to Settings',
  'Eigener Key entfernt, läuft wieder über unseren Zugang.': 'Your own key was removed — using our shared access again.',
  'Eigener Key entfernt.': 'Your own key was removed.',
  'Prüfung ging nicht': 'Check failed',
  'Der Anbieter hat diesen Key abgelehnt.': 'The provider rejected this key.',
  'In den Agenten wählbar.': 'Selectable in agents.',
  'In der Server-Umgebung (Render) ist kein {label}-Key hinterlegt. Dort eintragen — oder hier einen eigenen Key speichern.': 'No {label} key is set in the server environment (Render). Add it there — or save your own key here.',
  'Ohne Key nicht wählbar.': 'Not selectable without a key.',
  'Key abgelehnt': 'Key rejected',
  'aktiv': 'active',
  'Server-Key fehlt': 'Server key missing',
  'inaktiv': 'inactive',
  'EIGENER KEY': 'OWN KEY',
  'Funktioniert nicht mehr': 'No longer working',
  'Ändern': 'Change',
  'Prüfen': 'Verify',
  'Entfernen': 'Remove',
  'Key eintragen': 'Add key',
  'Bitte den Key einfügen.': 'Please paste the key.',
  '{label} braucht zusätzlich ein Secret.': '{label} also requires a secret.',
  'Key ungültig.': 'Invalid key.',
  '{label} ist eingerichtet: {detail}': '{label} is set up: {detail}',
  'Die Prüfung ging nicht.': 'The check failed.',
  '{label}-Key ersetzen': 'Replace {label} key',
  '{label}-Key eintragen': 'Add {label} key',
  'API-Key einfügen': 'Paste API key',
  'API-Secret': 'API secret',
  'Key ungültig': 'Invalid key',
  'Wird erst bei {label} getestet und nur gespeichert, wenn er dort angenommen wird.': 'Tested with {label} first, and only saved once it’s accepted there.',
  'Key holen →': 'Get a key →',
  'Wird geprüft …': 'Verifying…',
  'Prüfen & speichern': 'Verify & save',
  'TikTok wird gefragt, was dieses Konto darf…': 'Asking TikTok what this account is allowed to do…',
  'Geht als Entwurf in die TikTok-App': 'Goes to the TikTok app as a draft',
  'Direktposten ist für dieses Konto nicht möglich.': 'Direct posting isn’t available for this account.',
  'Für dieses Projekt ist eingestellt, dass TikTok-Beiträge als Entwurf in deine TikTok-Inbox gehen. Ändern lässt sich das unter Automatik.': 'This project is set to send TikTok posts as drafts to your TikTok inbox. You can change that under Automation.',
  'Dieses Konto wurde ohne die Berechtigung zum Direktposten verbunden. Nach der Freigabe durch TikTok einmal neu verbinden.': 'This account was connected without the direct-post permission. Reconnect once TikTok has approved the app.',
  'TikTok hat das Direktposten für diese App noch nicht freigegeben. Bis dahin geht jeder Beitrag als Entwurf in die TikTok-App.': 'TikTok has not approved direct posting for this app yet. Until then every post goes to the TikTok app as a draft.',
  'Du legst dort die Musik aus TikToks Katalog drauf und postest selbst — das trägt ohnehin weiter als eingerenderte Musik.': 'You add music from TikTok’s catalog there and post it yourself — that reaches further anyway than music baked into the video.',
  'Zu lang für dieses Konto': 'Too long for this account',
  'Der Beitrag dauert {seconds} Sekunden, das Konto erlaubt {max}. Kürze ihn im Story Builder, sonst weist TikTok ihn nach dem Rendern ab.': 'The post is {seconds} seconds long, but this account allows {max}. Trim it in the Story Builder, or TikTok will reject it after rendering.',
  'TikTok-Konto': 'TikTok account',
  'geht direkt auf TikTok': 'posts directly to TikTok',
  'Wer soll das sehen?': 'Who should see this?',
  'Bitte wählen': 'Please choose',
  'Sichtbarkeit': 'Visibility',
  'Kommentare erlauben': 'Allow comments',
  'Duett erlauben': 'Allow Duet',
  'Stitch erlauben': 'Allow Stitch',
  'Was dürfen andere damit machen?': 'What can others do with this?',
  'Vom TikTok-Konto selbst deaktiviert': 'Disabled by the TikTok account itself',
  'Bezahlte Partnerschaft': 'Paid partnership',
  'Werbeinhalt': 'Promotional content',
  'Ist das Werbung?': 'Is this an ad?',
  'Eigenwerbung': 'Self-promotion',
  'Der Beitrag bewirbt das eigene Geschäft oder Produkt': 'The post promotes your own business or product',
  'Ein Dritter hat für diesen Beitrag bezahlt. Dann ist „Nur ich" nicht möglich.': 'A third party paid for this post. In that case, "Only me" isn’t available.',
  'TikTok blendet am Beitrag': 'TikTok will show',
  'ein. Es gelten die': 'on this post, per the',
  'Richtlinien für Markeninhalte': 'Branded Content Policy',
  'App-ID': 'App ID',
  'App-Geheimnis': 'App secret',
  'kopiert': 'copied',
  'Kopieren': 'Copy',
  '{name}-App gespeichert.': '{name} app saved.',
  'Speichern fehlgeschlagen': 'Save failed',
  'eingerichtet': 'set up',
  'aus .env': 'from .env',
  'Ohne diese App kann sich niemand mit {name} verbinden.': 'Without this app, no one can connect to {name}.',
  'Zuklappen': 'Collapse',
  'Einrichten': 'Set up',
  'Zugangsdaten': 'Credentials',
  '{portal} öffnen ↗': 'Open {portal} ↗',
  'Nur der Betreiber kann die Plattform-Apps ändern.': 'Only the operator can change the platform apps.',
  'Diese App ist über Umgebungsvariablen gesetzt und hat Vorrang. Zum Ändern die': 'This app is set via environment variables and takes priority. To change it, edit',
  'anpassen und API sowie Worker neu starten.': 'and restart the API and worker.',
  'gespeichert {hint}': 'saved {hint}',
  'einfügen': 'paste',
  'App speichern': 'Save app',
  'Audit bestanden': 'Audit passed',
  ', schaltet echtes Veröffentlichen frei': ', unlocks real publishing',
  'Verifizierte Domain': 'Verified domain',
  '(optional, macht den Upload schneller)': '(optional, speeds up uploads)',
  'Die Domain, unter der deine Medien öffentlich liegen, bei TikTok unter „URL properties" verifiziert. Dann holt TikTok die Datei selbst ab, statt dass wir sie blockweise hochladen.': 'The domain where your media is publicly hosted, verified with TikTok under "URL properties." TikTok then fetches the file itself instead of us uploading it in chunks.',
  'Tarif steht jetzt auf {plan}.': 'Plan is now set to {plan}.',
  '{n} Credits gutgeschrieben.': '{n} credits added.',
  '{n} Credits abgezogen.': '{n} credits deducted.',
  'Entwickler-Werkbank, nur lokal': 'Developer tools, local only',
  'Entwickler-Werkbank': 'Developer tools',
  'Tarif': 'Plan',
  'Projekt': 'Project',

  /* --- Batch 4: Music & misc widgets --- */
  'Für diesen Track ist keine Datei hinterlegt.': 'No file is attached to this track.',
  'Pause': 'Pause',
  'Abspielen': 'Play',
  'Läuft': 'Playing',
  'Bibliothek': 'Library',
  'Lautstärke': 'Volume',
  'Plan': 'Plan',
  'Bild': 'Image',
  'Video': 'Video',
  '{music} · Bibliothek': '{music} · Library',
  'frei': 'free',
  'Agenten öfter einplanen (+1× pro Woche)': 'Schedule the agent more often (+1× per week)',
  'Plant den Agenten': 'Adds the agent',
  '+1× pro Woche': '+1× per week',
  'ein. Die Termine verteilt der Fahrplan gleichmäßig.': 'to the schedule. Time slots are spread out automatically.',
  '{n} Post in sieben Tagen': '{n} post in seven days',
  '{n} Posts in sieben Tagen': '{n} posts in seven days',
  'höchstens {n} am Tag': 'at most {n} per day',
  'Uhrzeiten': 'Times',
  'Übernehmen': 'Apply',
  'Als Nächstes': 'Up next',
  'Beispiel folgt': 'Example coming soon',
  'zurück': 'back',
  'weiter': 'next',
  'zurzeit {label}': 'currently {label}',
  'Standard': 'Standard',
  'empfohlen': 'recommended',
  'viel Textplatz': 'plenty of text room',
  'kurze Zeile': 'short line',
  'du beschreibst den Look': 'you describe the look',
  'Wird jedem Bildprompt vorangestellt — ansehen': 'Added to the front of every image prompt — view it',
  'Dein Text wird jedem Bildprompt vorangestellt.': 'Your text is added to the front of every image prompt.',
  'Ohne festen Stil wählt die AI pro Post den Look, der zum Thema passt.': 'Without a fixed style, the AI picks the look that fits each post’s topic.',
  'z. B. weiche Pastelltöne, Studiolicht, sehr viel Weißraum': 'e.g. soft pastel tones, studio lighting, lots of white space',
  'Wird jedem Bildprompt vorangestellt. Beschreibe den Look, nicht das Motiv.': 'Added to the front of every image prompt. Describe the look, not the subject.',
  'TikTok Video': 'TikTok Video',
  'TikTok Fotos, zum Durchwischen': 'TikTok Photos, swipe through',
  'Instagram Reels': 'Instagram Reels',
  'Instagram Feed, Karussell zum Durchwischen': 'Instagram Feed, swipe-through carousel',
  'Instagram Story': 'Instagram Story',
  'Fotos': 'Photos',
  'Reels': 'Reels',
  'Feed': 'Feed',
  'Story': 'Story',
  'Tracks für alle Projekte, beim Rendern fest untergelegt': 'Tracks for all projects, baked into the render as background audio.',
  'Musik finden': 'Find music',
  'Eigene MP3': 'Custom MP3',
  'Keine Tracks in dieser Auswahl': 'No tracks in this selection',
  'Such im offenen Katalog nach Musik, die kommerziell genutzt werden darf, mit einem Klick landet sie hier.': 'Search the open catalog for music cleared for commercial use — one click adds it here.',
  'Titel': 'Title',
  'Genre': 'Genre',
  'Stimmung': 'Mood',
  'Länge': 'Length',
  'Lizenz': 'License',
  'Namensnennung nötig': 'Attribution required',
  '„{title}" aus der Bibliothek löschen?': 'Delete "{title}" from the library?',
  'Gelöscht.': 'Deleted.',
  'Wie die Musik in den Post kommt': 'How the music gets into the post',
  'Track wählen': 'Choose a track',
  'Die AI passt Stimmung und Länge an, oder du legst einen festen Track fest.': 'The AI matches mood and length, or you set a fixed track.',
  'FFmpeg mischt': 'FFmpeg mixes it in',
  'Szenen, Texte und Audio werden zu einem MP4 mit fertigem Ton.': 'Scenes, text, and audio are combined into one MP4 with the finished soundtrack.',
  'Upload': 'Upload',
  'Beide Plattformen akzeptieren Videos mit eigener Tonspur.': 'Both platforms accept videos with their own audio track.',
  'Trending-Sounds': 'Trending sounds',
  'Gibt die API nicht her. Nur über den TikTok-Entwurf: Sound wählst du in der App.': 'Not available through the API. Only via the TikTok draft: you pick the sound in the app.',
  'Business-Konto?': 'Business account?',
  'Dann in der TikTok-App nur Sounds aus der Commercial Music Library nehmen. Ein normaler Chart-Song wird bei gewerblichen Konten stummgeschaltet.': 'Then only use sounds from the Commercial Music Library in the TikTok app. A regular chart song gets muted on business accounts.',
  'Suche fehlgeschlagen': 'Search failed',
  '„{title}" ist jetzt in deiner Bibliothek.': '"{title}" is now in your library.',
  'Import fehlgeschlagen': 'Import failed',
  'Offene Kataloge über Openverse, nur Lizenzen, die kommerzielle Nutzung erlauben.': 'Open catalogs via Openverse, only licenses that allow commercial use.',
  'Katalog durchsuchen': 'Search the catalog',
  'Über Link einfügen': 'Paste a link instead',
  'z. B. lofi beat, cinematic tension, upbeat house…': 'e.g. lofi beat, cinematic tension, upbeat house…',
  'Suchen': 'Search',
  'Nur CC0': 'CC0 only',
  ', komplett frei, keine Namensnennung nötig. Empfohlen, spart später Ärger.': ', completely free, no attribution required. Recommended — saves you trouble later.',
  'Katalog wird durchsucht…': 'Searching the catalog…',
  'Nichts gefunden. Versuch einen englischen Suchbegriff, die Kataloge sind überwiegend englisch verschlagwortet.': 'Nothing found. Try an English search term — the catalogs are tagged mostly in English.',
  'Quelle': 'Source',
  'drin': 'added',
  'Zur Lizenz:': 'About licensing:',
  'CC0 ist gemeinfrei, du kannst damit machen, was du willst. Bei CC-BY musst du den Urheber nennen; die AI Feed Factory speichert den nötigen Text automatisch mit und zeigt ihn in der Bibliothek an. In den Feed gehört er dann in die Caption. Übernommene Tracks werden auf 60 Sekunden gekürzt und nach MP3 gewandelt.': 'CC0 is public domain — you can do anything you like with it. With CC-BY you must credit the creator; AI Feed Factory automatically saves the required text and shows it in the library. It then belongs in the caption of your feed post. Imported tracks are trimmed to 60 seconds and converted to MP3.',
  'kostenlos, kommerziell nutzbar, keine Namensnennung': 'free, commercial use allowed, no attribution needed',
  'CC-Lizenzen, Filter auf kommerziell möglich': 'CC licenses, filterable for commercial use',
  'Kevin MacLeod, CC-BY, Namensnennung nötig': 'Kevin MacLeod, CC-BY, attribution required',
  'viel CC0, Lizenz pro Datei prüfen': 'lots of CC0, check the license per file',
  'Die Katalogsuche ist gerade gedrosselt. Über den direkten Link klappt es trotzdem, hol dir einen Track aus einer der Quellen unten und füg die Datei-Adresse hier ein.': 'The catalog search is currently rate-limited. A direct link still works — grab a track from one of the sources below and paste the file address here.',
  'Direkte Adresse der Audiodatei': 'Direct address of the audio file',
  'Die Adresse muss direkt auf die Audiodatei zeigen, nicht auf die Seite drumherum. Rechtsklick auf den Download-Knopf → „Link kopieren".': 'The address must point directly to the audio file, not the page around it. Right-click the download button → "Copy link".',
  'Titel (optional)': 'Title (optional)',
  'wird sonst aus dem Dateinamen gelesen': 'otherwise taken from the file name',
  'Urheber (optional)': 'Creator (optional)',
  'z. B. Kevin MacLeod': 'e.g. Kevin MacLeod',
  'Gekauft/lizenziert': 'Purchased/licensed',
  'Eigene Produktion': 'Own production',
  'Pflicht bei CC-BY. Dieser Text gehört später in die Caption.': 'Required for CC-BY. This text belongs in the caption later.',
  '"Titel" von Urheber, CC BY 4.0': '"Title" by creator, CC BY 4.0',
  'Ich habe geprüft, dass dieser Track kommerziell genutzt werden darf. Das kann dir niemand abnehmen, und beide Plattformen erkennen fremde Musik automatisch.': 'I’ve checked that this track may be used commercially. No one else can verify this for you, and both platforms detect third-party music automatically.',
  'Gute Quellen': 'Good sources',
  'eigen': 'Own',
  'lizenziert': 'Licensed',
  'Eigene MP3 hochladen': 'Upload custom MP3',
  'Bis 25 MB. Steht danach allen Projekten deiner Organisation zur Verfügung.': 'Up to 25 MB. Once uploaded, it’s available to all projects in your organization.',
  'Datei': 'File',
  'Track hochgeladen.': 'Track uploaded.',
  'Upload fehlgeschlagen': 'Upload failed',
  'Energie': 'Energy',
  'Ruhig': 'Calm',
  'Spannung': 'Tension',
  'Cool': 'Cool',
  'Neutral': 'Neutral',
  'Spielerisch': 'Playful',
  'Ich habe die Rechte an diesem Track. Fremde Musik führt auf beiden Plattformen zur Stummschaltung, im Wiederholungsfall zur Sperre des Kontos.': 'I hold the rights to this track. Third-party music gets muted on both platforms, and repeated violations can get the account suspended.',

  /* --- Nachtrag: von den Batches ausgelassene Stellen --- */
  'Wirkt erst, wenn der Autopilot oben eingeschaltet ist.': 'Takes effect only once autopilot is switched on above.',
  'Anklicken plant einen Agenten ein. Alle zusammen dürfen höchstens': 'Clicking schedules an agent. Together they may publish at most',
  '— mehr wertet TikTok als Spam.': '— more than that and TikTok treats it as spam.',
  'Wochenplätze vergeben': 'Weekly slots used',
  'Noch kein Agent angelegt. Ohne Rezept kann der Autopilot nichts bauen —': 'No agent created yet. Without a recipe, the autopilot has nothing to build —',
  'Der Autopilot ist an, aber kein Agent eingeplant — es wird nichts gebaut.': 'The autopilot is on, but no agent is scheduled — nothing will be built.',
  '2× täglich': '2× daily',
  '3× täglich': '3× daily',
  '4× täglich': '4× daily',
  'Aus dem Fahrplan nehmen': 'Remove from the schedule',
  'In den Fahrplan aufnehmen': 'Add to the schedule',
  'Dafür sind nicht mehr genug Plätze in der Woche frei': 'Not enough slots left this week for that',
  'Screenshots der App': 'App screenshots',
  'Noch keine Screenshots. Solange keine da sind, zeigt kein Post die App.': 'No screenshots yet. Until there are some, no post shows the app.',
  'Wie oft ist die App zu sehen?': 'How often should the app appear?',
  'Kein Post zeigt die App. Die Screenshots bleiben ungenutzt.': 'No post shows the app. The screenshots stay unused.',
  'Sehr hoch. Ein Feed, der fast nur das Produkt zeigt, wird weggescrollt — 20 bis 40 Prozent wirken meist besser.': 'Very high. A feed that shows almost nothing but the product gets scrolled past — 20 to 40 percent usually works better.',
  'Track lässt sich nicht laden. Quelle erlaubt womöglich keinen direkten Zugriff.': 'Can’t load this track. The source may not allow direct access.',
  'Löschen fehlgeschlagen.': 'Deletion failed.',
  'Das lässt sich nicht rückgängig machen.': 'This cannot be undone.',
  'Danach ist alles weg, auch die fertigen Videos.': 'After this, everything is gone — including the finished videos.',
  'Ja, alles unwiderruflich löschen': 'Yes, delete everything permanently',
  'Agent-Läufe samt Protokoll': 'agent runs, including their logs',
  'Account wird getrennt': 'account will be disconnected',
  'Accounts werden getrennt': 'accounts will be disconnected',
  'Wird ermittelt…': 'Calculating…',
  'zum Bestätigen': 'to confirm',
  'Stimmt noch nicht überein.': 'Doesn’t match yet.',
  ', abgerechnete Credits bleiben nachvollziehbar, auch wenn das Projekt weg ist.': ', billed credits remain traceable even after the project is gone.',
  'Soll wirklich alles gelöscht werden?': 'Do you really want to delete everything?',
  'Es gibt kein Zurück und keinen Papierkorb.': 'There’s no going back, and no recycle bin.',
  'Werkbank nicht erreichbar.': 'Dev tools unreachable.',
  'Reservierungen freigeräumt.': 'Reservations cleared.',
  'nur lokal · nicht im Livebetrieb': 'local only · not in production',
  'Bezahlte Tarife schreiben ihr Monatsguthaben gleich mit gut.': 'Paid plans credit their monthly allowance immediately.',
  'Guthaben auf null setzen, um leere Kassen zu testen': 'Set balance to zero, to test running out of credits',
  'auf 0': 'to 0',
  'Reservierungen freiräumen': 'Clear reservations',
  'Abgebrochene Läufe lassen Credits geblockt zurück. Das gibt sie wieder frei.': 'Aborted runs leave credits locked. This releases them again.',
  'Die Datei ist größer als 2 MB. Das ist kein Briefing mehr, sondern ein Buch.': 'The file is bigger than 2 MB. That’s not a briefing anymore, that’s a book.',
  'Die Datei ist leer.': 'The file is empty.',
  'Die Datei ließ sich nicht lesen.': 'The file could not be read.',
  'Markdown oder Text hierher ziehen, etwa die': 'Drag markdown or text here, such as the',
  'Datei wählen': 'Choose file',
  '— Servervorgabe, am Agenten ist nichts eingestellt': '— server default, nothing set on the agent',
  'Mit dem Veröffentlichen bestätigst du TikToks': 'By publishing, you confirm TikTok’s',
  'fehlt noch': 'missing',
  'Redirect-URI für das Portal': 'Redirect URI for the portal',
  'Zeitplan übernommen.': 'Schedule applied.',
  'Zu welchen Tageszeiten gepostet wird. Wie viele Posts es sind, sagt der Fahrplan darunter.': 'Which times of day posts go out. How many posts that is comes from the schedule below.',
  'Ohne ein einziges Zeitfenster kann nichts geplant werden.': 'Without at least one time window, nothing can be scheduled.',
  'Lauf abbrechen? Bereits gebaute Entwürfe bleiben erhalten.': 'Abort the run? Drafts already built are kept.',
  'Lauf wird fortgesetzt.': 'Run is resuming.',
  'Fortsetzen ging nicht': 'Resuming failed',
  'Ein früherer Lauf': 'A previous run',
  'Der Agent arbeitet': 'The agent is working',
  'Zum aktuellen Lauf': 'Go to current run',
  'Wartet auf den nächsten Termin': 'Waiting for the next scheduled time',
  'Der Agent entwirft gerade die Hooks und Themen. Gleich erscheinen die Posts hier.': 'The agent is drafting hooks and topics right now. The posts will appear here shortly.',
  'Autopilot ist aus, Läufe startest du selbst.': 'Autopilot is off, you start runs yourself.',
  'Ein laufender Generator wird noch zu Ende geführt': 'A generator already running will still be finished',
  'Noch kein Lauf': 'No run yet',
  'Wieder dem Agenten folgen': 'Follow the agent again',
  'Bildprompt an die AI': 'Image prompt sent to the AI',
  'Text, der ins Bild gebrannt wird': 'Text burned into the image',
  'keine Zeile in dieser Szene': 'no line in this scene',
  'wird gewählt…': 'being chosen…',
  'noch nicht gewählt': 'not chosen yet',
  'Fertige Entwürfe ansehen': 'View finished drafts',
  'daraus baut er als Nächstes': 'built from next',
  'Leer, der Agent füllt ihn nach jedem Lauf selbst auf.': 'Empty — the agent refills it itself after every run.',
  'Noch keine Ausgaben.': 'No output yet.',

  /* --- Agenten: Karte, Editor, Wochenplan --- */
  '{n}× / Woche': '{n}× / week',
  '{n} Szenen': '{n} scenes',
  '{range} Szenen': '{range} scenes',
  'Credits je 1.000 Views': 'Credits per 1,000 views',
  'Dieser Agent arbeitet gerade': 'This agent is working right now',
  'Duplizieren': 'Duplicate',
  'eigene Anweisung': 'custom instruction',
  'gebaut': 'built',
  'je 1k Views': 'per 1k views',
  'Läuft…': 'Running…',
  'Ziel dieses Agenten': 'What this agent is for',
  'zuletzt gelaufen {ago}': 'last run {ago}',
  '{n} % Videos': '{n}% video',
  'Views': 'Views',
  '{n} Posts bauen': 'Build {n} posts',
  '{label} · Video ist der Treiber': '{label} · video is the cost driver',
  'Agent angelegt.': 'Agent created.',
  'Agent gespeichert.': 'Agent saved.',
  'AI darf variieren': 'AI may vary it',
  'Anlegen': 'Create',
  'Bei einem Lauf von Hand wird der Bogen über alle Posts dieses Laufs erzählt. Im Autopilot läuft die Serie über mehrere Termine — jeder Platz baut den nächsten Teil, und im Bild steht „Teil 2/4".': 'On a manual run, the arc spans every post of that run. On autopilot the series runs across several slots — each slot builds the next part, and the image says “Part 2/4”.',
  'Bei etwa {n} % der Posts ist die App im Bild.': 'The app appears in roughly {n}% of posts.',
  'Bitte einen Namen angeben.': 'Please enter a name.',
  'Call-to-Action': 'Call to action',
  'eigener Key': 'own key',
  'Ein Post ist entweder ganz Bild-Story oder ganz Video, nie gemischt. Ein Video-Post ist immer EIN Clip; die Szenen unten gelten nur für Bild-Posts.': 'A post is either all image story or all video, never mixed. A video post is always ONE clip; the scene count below applies to image posts only.',
  'Eine Serie erzählt über mehrere Posts EINE Geschichte — wer Teil 1 gesehen hat, hat einen Grund, auf Teil 2 zu warten. Genau daran wachsen Konten.': 'A series tells ONE story across several posts — anyone who saw part 1 has a reason to wait for part 2. That’s exactly how accounts grow.',
  'Eine Szene ist ein Bild mit einer Textzeile darauf. Mehrere Szenen ergeben die Slideshow, durch die man wischt, mit Musik darunter.': 'A scene is one image with a line of text on it. Several scenes make the slideshow you swipe through, with music underneath.',
  'Farbe {c}': 'Color {c}',
  'Gilt INNERHALB eines Posts. Ob mehrere Posts zusammen eine Geschichte erzählen, entscheidet weiter unten die Einstellung Serien.': 'Applies WITHIN a single post. Whether several posts tell one story together is decided by the Series setting further down.',
  'je Lauf ({n} Posts)': 'per run ({n} posts)',
  'Jeder Post ist ein eigener Beitrag mit eigenem Hook, eigener Caption und eigenem Sendetermin. Der Autopilot ignoriert die Zahl und baut je Termin genau einen.': 'Each post is its own piece, with its own hook, caption and publishing slot. The autopilot ignores this number and builds exactly one per slot.',
  'kein Zugang': 'no access',
  'leer = wie im Projekt': 'empty = as in the project',
  'Medien, grob': 'Media, roughly',
  'Neuer Agent': 'New agent',
  'Sek.': 'sec',
  'Server-Vorgabe': 'Server default',
  'Standardmodell': 'Default model',
  'Text- & Planungsmodell': 'Text & planning model',
  'Video-Posts haben davon unabhängig immer genau einen Clip.': 'Video posts always have exactly one clip, regardless of this.',
  'Was soll dieser Agent erreichen?': 'What should this agent achieve?',
  'Wie im Projekt · {label}': 'As in the project · {label}',
  'Wie im Projekt · AI entscheidet': 'As in the project · AI decides',
  'z. B. Anime-Bilder': 'e.g. anime images',
  'Post': 'Post',
  'je Post': 'per post',
  ' Credits / Monat': ' credits / month',
  ' Credits / Woche': ' credits / week',
  '{n} Posts pro Woche aus {count} {agentWord} kosten': '{n} posts per week from {count} {agentWord} cost',
  '{used} von {max}': '{used} of {max}',
  '1× / Woche': '1× / week',
  '3× / Woche': '3× / week',
  'Agenten →': 'Agents →',
  'Alle {n} Plätze der Woche sind vergeben. Nimm zuerst bei einem anderen Agenten die Häufigkeit herunter.': 'All {n} slots this week are taken. Lower the frequency on another agent first.',
  'Cr.': 'cr.',
  'Credits / Woche': 'Credits / week',
  'Fahrplan': 'Schedule',
  'hier anlegen': 'create one here',
  'täglich': 'daily',
  'Zusammen wären das {n} Posts pro Woche. Ein Projekt darf höchstens {perDay} am Tag veröffentlichen, also {perWeek} pro Woche.': 'Together that would be {n} posts per week. A project may publish at most {perDay} per day, so {perWeek} per week.',

  /* --- Assistent --- */
  'arbeitet an {name}': 'working on {name}',
  'Assistent-Fehler': 'Assistant error',
  'denkt nach…': 'thinking…',
  'Sag einfach, was passieren soll. Ich lege Projekte an, plane Posts, schreibe Captions um, stelle den Zeitplan ein und gebe Entwürfe frei, du schaust zu.': 'Just say what should happen. I create projects, plan posts, rewrite captions, set the schedule and approve drafts — you watch.',
  'übergreifend': 'across projects',
  'Verbindung abgebrochen: {err}': 'Connection lost: {err}',
  'Was soll ich tun?': 'What should I do?',
  'Steckengeblieben?': 'Stuck?',

  /* --- Projekt loeschen, Entwicklerwerkzeuge --- */
  '„{name}" gelöscht, {posts} Posts und {files} Dateien entfernt.': '“{name}” deleted — {posts} posts and {files} files removed.',
  'Dateien': 'Files',
  'Läufe': 'Runs',
  'von': 'of',
  'Guthaben': 'Balance',

  /* --- Unterlagen und Briefing --- */
  ' (etwa {tokens} Token je Lauf)': ' (about {tokens} tokens per run)',
  '{n} Unterlage hinzugefügt.': '{n} document added.',
  '{n} Unterlagen hinzugefügt.': '{n} documents added.',
  '{n} Zeichen': '{n} characters',
  'ansehen': 'view',
  'Auftrag': 'Brief',
  'Briefing gespeichert.': 'Briefing saved.',
  'leer': 'empty',
  'Nichts Lesbares dabei.': 'Nothing readable in there.',
  'Post-Ideen': 'Post ideas',
  'Post-Ideen gespeichert.': 'Post ideas saved.',
  'schreiben': 'write',
  'Unterlage {n}': 'Document {n}',
  'Unterlage entfernen': 'Remove document',
  'Unterlage entfernt.': 'Document removed.',
  'Unterlage gespeichert.': 'Document saved.',
  'Weitere Datei': 'Another file',
  'Wird vor jedem Post mitgelesen. Zusammen {chars} Zeichen': 'Read before every post. {chars} characters in total',
  'Ziemlich viel. Alles davon wird bei jedem einzelnen Lauf mitgeschickt und kostet entsprechend Credits. Mehr Unterlagen machen die Posts nicht automatisch besser — nur das, was wirklich beschreibt, was die App für Menschen tut.': 'That’s quite a lot. All of it is sent along on every single run and costs credits accordingly. More documents don’t automatically make better posts — only the ones that really describe what the app does for people.',
  'zu': 'to',
  'Briefing schreiben': 'Write briefing',

  /* --- Rahmen und Navigation --- */
  '{balance} Credits · {reserved} reserviert': '{balance} credits · {reserved} reserved',
  '{count} Projekte arbeiten': '{count} projects working',
  '{name} arbeitet': '{name} is working',
  'Projekt-Navigation': 'Project navigation',
  'Tarif {plan}': '{plan} plan',
  ' — Servervorgabe, am Agenten ist nichts eingestellt': ' — server default, nothing set on the agent',
  '— also dass die Musik im Video entweder nicht geschützt ist oder du alle nötigen Rechte daran hast.': '— meaning the music in the video is either unprotected or you hold all the rights you need to it.',

  /* --- Tarife, Pakete, Abrechnung --- */
  ' · {n} Credits verfügbar': ' · {n} credits available',
  'Aktueller Tarif:': 'Current plan:',
  'Lieber einmalig nachladen statt Abo? Credit-Pakete laufen nicht ab und lösen Guthaben-Grenzen, Projekt-Grenzen aber nicht.': 'Prefer a one-off top-up over a subscription? Credit packs never expire and lift balance limits — but not project limits.',
  'Später': 'Later',
  'Tarife': 'Plans',
  ' · {n} Accounts je Projekt': ' · {n} accounts per project',
  '≈ {n} fertige Bild-Beiträge': '≈ {n} finished image posts',
  '{n} Credits / Monat': '{n} credits / month',
  '{name} holen': 'Get {name}',
  'bis zu {n}': 'up to {n}',
  'Empfohlen': 'Recommended',
  'Monatlich kündbar. Nicht verbrauchte Credits wandern bis zur Tarifgrenze in den Folgemonat. Reicht das Monatsguthaben nicht, lädst du jederzeit ein Credit-Paket nach, das läuft nicht ab.': 'Cancel monthly. Unused credits roll over into the next month up to your plan’s cap. If the monthly balance isn’t enough, top up with a credit pack any time — those never expire.',
  'pro Monat': 'per month',
  'Aktueller Tarif': 'Current plan',
  'Beliebt': 'Popular',
  '≈ {price}': '≈ {price}',
  '{n} Credits fuer {name} gutgeschrieben.': '{n} credits credited to {name}.',
  '{n} Credits je Post': '{n} credits per post',
  '{n} Credits pro Monat · bis zu {m}': '{n} credits per month · up to {m}',
  '{n} für laufende Jobs geblockt': '{n} held for running jobs',
  '{n} Tage': '{n} days',
  '{n} veröffentlicht': '{n} published',
  '{price} / Credit': '{price} / credit',
  '{price} / Monat': '{price} / month',
  '30 Tage': '30 days',
  'Abo verwalten': 'Manage subscription',
  'Anbieterkosten': 'Provider cost',
  'Aufladen': 'Top up',
  'Automatisch aufladen': 'Auto top-up',
  'Automatisches Aufladen an.': 'Auto top-up on.',
  'Automatisches Aufladen aus.': 'Auto top-up off.',
  'beim aktuellen Verbrauch': 'at the current burn rate',
  'Credits / Tag': 'Credits / day',
  'Credits einmalig': 'One-off credits',
  'Credits verbraucht': 'Credits used',
  'davon zahlend': 'of them paying',
  'Deckungsbeitrag': 'Contribution margin',
  'Deckungsbeitrag {amount}': 'Contribution margin {amount}',
  'Fällt das Guthaben unter {n} Credits, kaufen wir automatisch nach, damit der Autopilot nicht mitten in der Woche stehen bleibt.': 'If the balance drops below {n} credits, we top up automatically so the autopilot doesn’t stall mid-week.',
  'Gegenwert': 'Value',
  '1 Hook, Caption & Hashtags': '1 hook, caption & hashtags',
  'gekauft': 'purchased',
  'geschenkt': 'granted',
  'Heute verbraucht': 'Used today',
  'inkl. {n} Bonus · ': 'incl. {n} bonus · ',
  'Kauf abgebrochen.': 'Purchase cancelled.',
  'Kaufen': 'Buy',
  'Kunde': 'customer',
  'Kunden': 'customers',
  'Letzte Buchungen': 'Recent entries',
  'Limit {n}': 'Limit {n}',
  'Limit gespeichert.': 'Limit saved.',
  'Monatstarife': 'Monthly plans',
  'Ø Kosten:': 'Avg. cost:',
  'Ø pro Post:': 'Avg. per post:',
  'Ø Views:': 'Avg. views:',
  'Pakete': 'Packs',
  'Pakete lösen Guthaben-Grenzen, aber keine Tarif-Grenzen: Wie viele Projekte parallel laufen dürfen, hängt weiter am Monatstarif.': 'Packs lift balance limits, not plan limits: how many projects may run in parallel still depends on the monthly plan.',
  'Rohmarge': 'Gross margin',
  'Sicherheitsnetz': 'Safety net',
  'Stand: {n} Credits': 'Currently {n} credits',
  'Umsatz': 'Revenue',
  'unsere Anbieterkosten': 'our provider cost',
  'Unsere Anbieterkosten': 'Our provider cost',
  'Verbrauch': 'Usage',
  'Verfügbar': 'Available',
  'Wirtschaftlichkeit · letzte 30 Tage': 'Unit economics · last 30 days',
  'verbraucht': 'used',
  'Dein Konto zahlt keine Credits, abgebucht wurde nichts.': 'Your account pays no credits; nothing was charged.',
  'Kosten': 'Cost',

  /* --- Freigaben --- */
  '{n} Entwürfe aus allen Projekten warten auf dich': '{n} drafts from all projects are waiting on you',
  'Diese Wahl gilt für alle {n} Beiträge. Einzeln abweichen kannst du, indem du den Beitrag öffnest und dort freigibst.': 'This choice applies to all {n} posts. To differ on a single one, open that post and approve it there.',
  'Freigabe fehlgeschlagen': 'Approval failed',
  'Nichts offen': 'Nothing pending',
  'werden gerendert und zur jeweils geplanten Zeit auf den verbundenen Konten veröffentlicht. Das Rendern verbraucht Credits.': 'will be rendered and published to the connected accounts at their scheduled times. Rendering uses credits.',
  'Zu den Projekten': 'Go to projects',
  '{n} Entwürfe freigeben?': 'Approve {n} drafts?',
  '{n} Entwürfe freigegeben.': '{n} drafts approved.',
  'Alle {n} freigeben': 'Approve all {n}',
  'Ja, freigeben': 'Yes, approve',
  'Nichts in dieser Auswahl': 'Nothing in this selection',
  'Alle {n} auswählen': 'Select all {n}',
  'alle': 'all',
  'Agent:': 'Agent:',
  '+ Post selbst bauen': '+ Build a post yourself',

  /* --- Uebersicht der Projekte --- */
  '{n} Entwurf wartet auf dich': '{n} draft is waiting on you',
  '{n} Entwürfe warten auf dich': '{n} drafts are waiting on you',
  '{n} Freigaben': '{n} approvals',
  '{n} Projekt hat noch keinen Account': '{n} project has no account yet',
  '{n} Projekte haben noch keinen Account': '{n} projects have no account yet',
  '{n} zur Freigabe': '{n} to approve',
  '{p} % fertig': '{p}% done',
  '{plan} erlaubt {max}, hier klicken für mehr': '{plan} allows {max} — click here for more',
  'alles erledigt': 'all clear',
  'App beschreiben, Accounts verbinden, ersten Agenten anlegen': 'Describe the app, connect accounts, create your first agent',
  'Arbeitet gerade': 'Working now',
  'Beschreib einmal, was deine App macht. Danach erfindet die AI die Hooks, generiert Bilder und Clips, schreibt Caption und Hashtags, legt Musik drunter, rendert fertige Videos und stellt sie zur richtigen Zeit auf TikTok, Instagram und Facebook. Du gibst frei, mehr nicht.': 'Describe once what your app does. After that the AI invents the hooks, generates images and clips, writes caption and hashtags, lays music underneath, renders finished videos and posts them to TikTok, Instagram and Facebook at the right time. You approve — that’s all.',
  'Credits / 30 T.': 'credits / 30 d.',
  'Dein Tarif {plan} umfasst {max} {maxWord}, {used} {usedWord} schon angelegt.': 'Your {plan} plan includes {max} {maxWord}; {used} {usedWord} already created.',
  'Erstes Projekt anlegen': 'Create your first project',
  'Freigaben offen': 'Approvals open',
  'ist': 'is',
  'Live': 'Live',
  'Neues Projekt anlegen': 'Create new project',
  'nichts offen': 'nothing pending',
  'Ohne verbundenen TikTok- oder Instagram-Account kann nichts veröffentlicht werden. Der Agent baut zwar Entwürfe, sie bleiben aber liegen.': 'Without a connected TikTok, Instagram or Facebook account nothing can be published. The agent still builds drafts, but they just sit there.',
  'pausiert': 'paused',
  'Queue': 'Queue',
  'sind': 'are',
  'Steht im Weg': 'In the way',
  'Tarifgrenze erreicht': 'Plan limit reached',
  'Verbindung in Ordnung': 'Connection fine',
  'Verbindung läuft aus': 'Connection expiring',
  'Views 30T': 'Views 30d',
  'Credits 30 Tage': 'Credits 30 days',
  'Views 30 Tage': 'Views 30 days',

  /* --- Post-Karte und Post-Ansicht --- */
  'Serie „{name}"': 'Series “{name}”',
  'TEIL {i}/{n}': 'PART {i}/{n}',
  'VIDEO': 'VIDEO',
  'Auswahl aufheben': 'Clear selection',
  '{n} Bilder': '{n} images',
  '{n} Cr.': '{n} cr.',
  '{n} BILD': '{n} IMAGE',
  'Auf {platform} ansehen ↗': 'View on {platform} ↗',
  'Diesen Entwurf verwerfen?': 'Discard this draft?',
  'Ergebnis': 'Result',
  'Format': 'Format',
  'Geht raus.': 'Going out.',
  'keine': 'none',
  'Kostenaufstellung ({n} Credits)': 'Cost breakdown ({n} credits)',
  'Neu schreiben': 'Rewrite',
  'Profil-Klicks': 'Profile clicks',
  'Saves': 'Saves',
  'Texte auf Szene {n}': 'Text on scene {n}',
  'unbekannt': 'unknown',
  'Verworfen.': 'Discarded.',
  'Ziel': 'Target',
  'ohne': 'without',
  'Caption': 'Caption',
  'Gespeichert.': 'Saved.',
  'Hashtags': 'Hashtags',
  'Neu geschrieben.': 'Rewritten.',
  'Verwerfen': 'Discard',
  'Freigegeben.': 'Approved.',
  'Auswählen': 'Select',
  'erstellt {ago}': 'created {ago}',
  'Erstellt am {date}': 'Created on {date}',
  'Kopiert': 'Copied',
  'Lädt…': 'Loading…',

  /* --- Zeitangaben (format.ts) --- */
  'gerade eben': 'just now',
  'gestern {time}': 'yesterday {time}',
  'heute {time}': 'today {time}',
  'morgen {time}': 'tomorrow {time}',
  'vor {n} Min.': '{n} min ago',
  'vor {n} Std.': '{n} h ago',
  'vor {n} Tg.': '{n} d ago',

  /* --- Musik --- */
  'Hochladen': 'Upload',
  'Namensnennung': 'Attribution',

  /* --- Agenten eines Projekts --- */
  '{count} von {max} · je ein Rezept: Stil, Szenen, Ziel, Modelle': '{count} of {max} · one recipe each: style, scenes, goal, models',
  '{plan} erlaubt {maxAgents} {noun} je Projekt. Hier klicken, um zu vergleichen, was mehr bringt.': '{plan} allows {maxAgents} {noun} per project. Click here to compare what more would give you.',
  'baut Post {current} von {total}': 'building post {current} of {total}',
  'Dein Tarif {plan} erlaubt {maxAgents} {noun} je Projekt.': 'Your {plan} plan allows {maxAgents} {noun} per project.',
  'Kopie angelegt.': 'Copy created.',
  'Neuen Agenten anlegen': 'Create new agent',
  'Tarif ansehen': 'View plans',
  '„{name}" läuft.': '“{name}” is running.',
  '{title} · zusehen →': '{title} · watch →',
  '{lo} bis {hi}': '{lo} to {hi}',
  '{n}× / Woche im Fahrplan': '{n}× / week in the schedule',
  '{n} % der Posts': '{n}% of posts',
  '{posts} Posts · {credits} Credits verbraucht': '{posts} posts · {credits} credits used',
  'Baut gerade Post {current} von {total}': 'Building post {current} of {total}',
  'Einstellen': 'Configure',
  'fester Titel': 'fixed title',
  'für': 'for',
  'geschätzt': 'estimated',
  'nur von „{name}" · anklicken zum Ansehen': 'only from “{name}” · click to view',
  'Was „{name}" gebaut hat': 'What “{name}” built',
  'zuletzt': 'last',
  'Live-Log': 'Live log',
  'Noch kein Bild fertig. Sobald der Generator das erste Bild zurückgibt, erscheint es hier, mit Prompt, Modell und den Textzeilen, die daraufkommen.': 'No image finished yet. As soon as the generator returns the first one it appears here, with prompt, model and the lines of text that go on it.',
  'Starte einen Lauf, oder schalte den Autopiloten ein, damit der Agent selbstständig nachlegt, sobald die Queue leer läuft.': 'Start a run, or switch on the autopilot so the agent tops up by itself whenever the queue runs dry.',
  '{n} Posts': '{n} posts',

  /* --- Story Builder --- */
  '♪ Musik': '♪ Music',
  '+ Neue Story': '+ New story',
  '+ Szene': '+ Scene',
  '+ Tag, Enter': '+ Tag, Enter',
  '+ Zeile': '+ Line',
  '1080×1920 · FFmpeg legt Szenen, Texte und Musik zu einem MP4 zusammen und lädt es über die Plattform-API hoch. Texte lassen sich direkt in der Vorschau ziehen.': '1080×1920 · FFmpeg combines scenes, text and music into one MP4 and uploads it through the platform API. Text can be dragged directly in the preview.',
  'Agenten planen lassen': 'Let an agent plan it',
  'AI schreiben lassen': 'Let the AI write it',
  'Anderer Post': 'Another post',
  'Bildprompt': 'Image prompt',
  'Der Bildstil des Projekts wird automatisch angehängt. Lass oben und unten ruhige Flächen frei, damit der Text lesbar bleibt.': 'The project’s image style is appended automatically. Leave calm areas at the top and bottom so the text stays readable.',
  'Generieren': 'Generate',
  'Links ↔ rechts': 'Left ↔ right',
  'Neu generiert.': 'Regenerated.',
  'Neue Story anlegen': 'Create new story',
  'Neue Zeile': 'New line',
  'Oben ↕ unten': 'Top ↕ bottom',
  'Reihenfolge gespeichert.': 'Order saved.',
  'Story Builder': 'Story builder',
  'Szene': 'Scene',
  'Szene {n} löschen?': 'Delete scene {n}?',
  'Szene {n} neu würfeln': 'Reroll scene {n}',
  'Szenen': 'Scenes',
  'Termin': 'Slot',
  'Texte in Szene {n}': 'Text in scene {n}',
  'Texte neu geschrieben.': 'Text rewritten.',
  'Texte neu schreiben': 'Rewrite text',
  'Textzeilen': 'Lines of text',
  'Video-Clip': 'Video clip',
  'Videoclips kosten deutlich mehr Credits als Bilder.': 'Video clips cost considerably more credits than images.',
  'Vorschau rendern': 'Render preview',
  'Wohin': 'Where to',
  'zusammen etwa {s} s': 'about {s} s in total',

  /* --- Projekt-Uebersicht und Queue --- */
  '≈ {n} pro Post': '≈ {n} per post',
  '{n} Follower': '{n} followers',
  '{n} T.': '{n} d.',
  '{n} Views': '{n} views',
  'ändern': 'change',
  'Autopilot an': 'Autopilot on',
  'Autopilot angehalten.': 'Autopilot paused.',
  'Der Agent erfindet Hooks, generiert die Medien, schreibt Captions und legt alles als Entwurf in die Queue. Veröffentlicht wird erst nach deiner Freigabe.': 'The agent invents hooks, generates the media, writes captions and puts everything into the queue as drafts. Nothing is published until you approve it.',
  'Fenster {labels} Uhr': 'Window {labels}',
  'Ganzer Plan →': 'Full plan →',
  'Historie': 'History',
  'insgesamt': 'in total',
  'Jetzt Posts bauen': 'Build posts now',
  'keins': 'none',
  'läuft…': 'running…',
  'letzte {n} veröffentlichte Posts': 'last {n} published posts',
  'Nochmal versuchen': 'Try again',
  'Posts bauen lassen': 'Have posts built',
  'Rezept:': 'Recipe:',
  'Schätzung fehlgeschlagen: {error}': 'Estimate failed: {error}',
  'Verbundene Accounts': 'Connected accounts',
  'Verfügbar: {n}': 'Available: {n}',
  'verwalten': 'manage',
  'Wie viele Posts?': 'How many posts?',
  'Zeitplan': 'Schedule',
  '{n} ausgewählt': '{n} selected',
  '{n} Post gelöscht.': '{n} post deleted.',
  '{n} Post löschen?': 'Delete {n} post?',
  '{n} Posts gelöscht.': '{n} posts deleted.',
  '{n} Posts löschen?': 'Delete {n} posts?',
  '{weg} von {total} gelöscht, der Rest ließ sich nicht entfernen.': '{weg} of {total} deleted; the rest could not be removed.',
  'Wird endgültig entfernt, samt erzeugter Bilder. Bereits veröffentlichte Beiträge bleiben auf TikTok, Instagram und Facebook stehen — gelöscht wird nur der Eintrag hier. Verbrauchte Credits kommen nicht zurück.': 'Removed for good, including the generated images. Posts already published stay up on TikTok, Instagram and Facebook — only the entry here is deleted. Credits already spent are not refunded.',

  /* --- Projekteinstellungen --- */
  'Beides optional. Steht die App in beiden Stores, passt die AI den Call-to-Action an die Plattform an, auf der der Post läuft.': 'Both optional. If the app is in both stores, the AI adapts the call to action to the platform the post runs on.',
  'Bild entfernen': 'Remove image',
  'Das Ziel ändert nicht die Stimmung, sondern den Bau jedes Posts: welches Muster der Hook hat, was die letzte Zeile tut, wie oft die App überhaupt im Bild vorkommt. Mehrere sind möglich — dann bestimmt das': 'The goal doesn’t change the mood but the construction of every post: which pattern the hook follows, what the last line does, how often the app appears at all. Several are possible — in that case the',
  'Entfernt': 'Removed',
  'Gilt für Hook, Textzeilen, Caption und Hashtags. Die Bildprompts bleiben immer englisch — das verlangen die Bildmodelle, und der Zuschauer sieht sie nie.': 'Applies to hook, lines of text, caption and hashtags. Image prompts always stay English — the image models require it, and viewers never see them.',
  'mit allem, was daran hängt: jeden Post, jedes generierte Bild, jedes gerenderte Video, alle Agent-Protokolle. Verbundene Accounts werden getrennt. Bereits veröffentlichte Beiträge bleiben auf TikTok, Instagram und Facebook online. Die liegen dort. Rückgängig machen lässt sich das nicht.': 'with everything attached to it: every post, every generated image, every rendered video, all agent logs. Connected accounts are disconnected. Posts already published stay online on TikTok, Instagram and Facebook. They remain there. This cannot be undone.',
  'Name gespeichert.': 'Name saved.',
  'Projektbild entfernt.': 'Project image removed.',
  'Projektbild gesetzt.': 'Project image set.',
  'Store-Link gespeichert.': 'Store link saved.',
  'Store-Links': 'Store links',
  'Untertitel': 'Subtitle',
  'Untertitel gespeichert.': 'Subtitle saved.',
  'Wie soll geredet werden?': 'How should it sound?',

  /* --- Einstellungen: Zugaenge, Anbieterpreise, Diagnose --- */
  '{n} Punkte solltest du dir ansehen': '{n} things you should look at',
  'Plattform-Apps': 'Platform apps',
  'Die Developer-Apps bei TikTok und Meta, einmal für die ganze Installation. Ohne sie bleibt der Verbinden-Knopf in jedem Projekt grau.': 'The developer apps at TikTok and Meta, once for the whole installation. Without them the connect button stays greyed out in every project.',
  'Konto': 'Account',
  'Angemeldet als': 'Signed in as',
  'Organisation': 'Organization',
  'Rolle': 'Role',
  'Eigentümer': 'Owner',
  'Administrator': 'Administrator',
  'Mitglied': 'Member',
  'Connectoren': 'Connectors',
  'Deine E-Mail steht in OPERATOR_EMAILS. Generierungen laufen über die Plattform-Keys aus der Server-Umgebung; eigene Keys sind zusätzlich möglich.': 'Your email is listed in OPERATOR_EMAILS. Generations run on the platform keys from the server environment; your own keys are possible on top.',
  'Deine E-Mail steht in BILLING_EXEMPT_EMAILS. Keine Credits nötig, aber alle Generierungen brauchen deine eigenen Anbieter-Keys.': 'Your email is listed in BILLING_EXEMPT_EMAILS. No credits needed, but every generation requires your own provider keys.',
  'Betreiberkonto': 'Operator account',
  'Freundeskonto': 'Friend account',
  'Kundenkonto': 'Customer account',
  'Startklar': 'Ready to go',
  'Es fehlt': 'Missing:',
  '. Solange bricht jeder Lauf mittendrin ab.': '. Until that’s fixed, every run breaks off halfway.',
  ' und ': ' and ',
  'Planung & Texte': 'Planning & text',
  'Replicate, Google, OpenAI, Recraft': 'Replicate, Google, OpenAI, Recraft',
  'Ein Key deckt oft mehrere Gattungen ab: Ein Google-Key bringt Planung, Nano Banana und Veo auf einmal mit, ein OpenAI-Key Planung und Bilder.': 'One key often covers several categories: a Google key brings planning, Nano Banana and Veo all at once; an OpenAI key brings planning and images.',
  'Als Betreiberkonto nutzt du die Keys aus den Render-Umgebungsvariablen (ANTHROPIC_API_KEY, REPLICATE_API_TOKEN, …) — solange dort nichts steht, bleibt hier alles inaktiv, egal welche Rechte dein Konto hat.': 'As an operator account you use the keys from the Render environment variables (ANTHROPIC_API_KEY, REPLICATE_API_TOKEN, …) — as long as nothing is set there, everything stays inactive here, no matter what rights your account has.',
  'Text & Planung': 'Text & planning',
  'Bild & Video': 'Image & video',
  '1000 Zeichen': '1,000 characters',
  'Für {models} veröffentlicht der Anbieter keinen Listenpreis. Wir rechnen mit einem Schätzwert plus 50 % Sicherheitsaufschlag, damit eine Fehlschätzung nicht die Marge frisst.': 'For {models} the provider publishes no list price. We use an estimate plus a 50% safety margin so a bad guess doesn’t eat the margin.',
  'Speicher, Rendering & Plattformen': 'Storage, rendering & platforms',
  'keine Schrift gefunden': 'no font found',
  'Szenen + Text + Musik → MP4, 1080×1920, 30 fps.': 'Scenes + text + music → MP4, 1080×1920, 30 fps.',
  'AI-Modell': 'AI model',
  'ANTHROPIC_API_KEY fehlt': 'ANTHROPIC_API_KEY missing',
  'TikTok-App': 'TikTok app',
  'Meta-/Instagram-App': 'Meta / Instagram app',
  'Zahlungen': 'Payments',
  'Stripe aktiv': 'Stripe active',
  'Warteschlange': 'Queue',
  'wartend': 'pending',
  'aufgegeben': 'given up',
  'Ein Projekt hat einen eigenen Feed, eigene Accounts und einen eigenen Zeitplan.': 'A project has its own feed, accounts and schedule.',
  'Was möchtest du bewerben?': 'What would you like to promote?',
  'Downloads und Nutzung': 'Downloads and usage',
  'Produkt': 'Product',
  'Verkäufe oder Affiliate-Link': 'Sales or affiliate link',
  'Unternehmen': 'Business',
  'Anfragen und Bekanntheit': 'Enquiries and awareness',
  'Branche': 'Industry',
  'z. B. Handwerk, Gastronomie': 'e.g. trades, hospitality',
  'Was macht ihr? (Pflicht)': 'What does your business do? (required)',
  'Leistungen und echte Stärken': 'Services and real strengths',
  'Ort oder Einzugsgebiet': 'Location or service area',
  'Website (optional)': 'Website (optional)',
  'Produkt- oder Affiliate-Link': 'Product or affiliate link',
  'Ich bewerbe das Produkt über einen Affiliate-Link': 'I promote this product with an affiliate link',
  'Jeder veröffentlichte Beitrag erhält automatisch „Werbung | Affiliate-Link“.': 'Every published post will automatically be marked as advertising with an affiliate link.',
  'Du bestimmst später je Kanal, ob ein Video oder eine Bildserie zum Durchwischen entsteht. Vor der Veröffentlichung kannst du den Beitrag prüfen.': 'You can later choose a video or swipeable image carousel for each channel. Review posts before publishing.',
  'Das Projekt entsteht sofort. Im Hintergrund verdichtet die AI dein Briefing und leitet erste Beitragsideen ab.': 'The project is created immediately. AI summarizes your brief and suggests post ideas in the background.',
  'Das Projekt entsteht sofort mit deinen Angaben. Ergänze danach Logo oder Produktfotos und verbinde die gewünschten Social-Media-Konten.': 'The project is created with your details. Then add a logo or product photos and connect your social accounts.',
  'Unternehmensprofil': 'Business profile',
  'Unternehmensprofil gespeichert.': 'Business profile saved.',
  'Website': 'Website',
  'Website gespeichert.': 'Website saved.',
  'Das Logo kannst du oben als Projektbild hochladen. Tatsachen für Posts stehen im Briefing und in den Unterlagen darunter.': 'Upload your logo above as the project image. Add facts for posts to the brief and documents below.',
  'Produktdaten': 'Product details',
  'Produktbeschreibung und belegte Eigenschaften': 'Product description and verified features',
  'Produktdaten gespeichert.': 'Product details saved.',
  'Produktlink gespeichert.': 'Product link saved.',
  'Affiliate-Kennzeichnung gespeichert.': 'Affiliate disclosure saved.',
  'Affiliate-Produkt: Beiträge als Werbung kennzeichnen': 'Affiliate product: mark posts as advertising',
  'Produktbilder kannst du oben als Projektbild und im Story Builder als Szenenmaterial hochladen.': 'Upload a product image above and use product photos as scene material in the Story Builder.',
  'Was ist das Produkt? Welche Eigenschaften kannst du belegen? Für wen ist es gedacht?': 'What is the product? Which features can you verify? Who is it for?',
  'Wer sind eure Kunden? Was macht euch besonders? Welche Aussagen sind belegbar?': 'Who are your customers? What makes you different? Which claims can you verify?',
  'Schreibe nur Fakten, die in Beiträgen verwendet werden dürfen. Preise und Versprechen bitte nur mit belegter Quelle.': 'Include only facts that may be used in posts. Add prices and promises only with a reliable source.',
  'Format je Kanal': 'Format by channel',
  'einzelner Bildpost': 'single image post',
  'wischbare Bildserie mit {n} Bildern': 'swipeable carousel with {n} images',
  'Ergebnis je Kanal': 'Result by channel',
  'Entwurf auf der Plattform': 'Draft on the platform',
  'Beitrag öffnen ↗': 'Open post ↗',
  'Produktbeschreibung': 'Product description',
  'Unternehmensbeschreibung': 'Business description',
  '{subject} prüfen': 'Review {subject}',
  'Die Grundlage steht. Ergänze später bei Bedarf weitere belegte Details.': 'The basics are in place. Add more verified details whenever needed.',
  'Beschreibe Angebot, Zielgruppe und Besonderheiten in mindestens {n} Zeichen. So kann die AI konkrete Posts schreiben.': 'Describe your offer, audience and strengths in at least {n} characters so AI can write specific posts.',
  'Guthaben prüfen': 'Check credits',
  'Guthaben ist vorhanden. Vor dem Start siehst du die konkrete Kostenschätzung.': 'Credits are available. You will see the cost estimate before starting.',
  'Für einen AI-Lauf brauchst du Guthaben. Die genauen Kosten zeigt die Vorschau vor dem Start.': 'You need credits for an AI run. The preview shows its cost before you start.',
  'Alle Schritte einklappen': 'Collapse all steps',
  'Alle Schritte zeigen': 'Show all steps',
  'Nichts ausgewählt heißt: Bildposts auf allen verbundenen Konten. Ein Bild bleibt ein einzelner Bildpost, ab zwei Bildern wird ein Karussell zum Durchwischen. Video entsteht nur bei ausdrücklich gewähltem Video-Ziel.': 'No selection means image posts on all connected accounts. One image stays a single post; two or more become a swipeable carousel. Video is created only when you explicitly select a video destination.',
  'TikTok Foto(s)': 'TikTok photo(s)',
  'Bitte nur Bilddateien auswählen.': 'Please select image files only.',
  'Instagram Feed erlaubt höchstens 10 Bilder.': 'Instagram Feed allows up to 10 images.',
  'TikTok erlaubt höchstens 35 Bilder.': 'TikTok allows up to 35 images.',
  '{n} Bild(er) hinzugefügt.': '{n} image(s) added.',
  'Upload fehlgeschlagen.': 'Upload failed.',
  'Füge zuerst mindestens ein Bild oder einen Clip hinzu.': 'Add at least one image or clip first.',
  'Wähle einen Kanal und das gewünschte Format.': 'Choose a channel and format.',
  'Ein Bildpost darf keine Videoszene enthalten. Entferne den Clip oder wähle ein Video-Ziel.': 'An image post cannot contain a video scene. Remove the clip or choose a video destination.',
  'Wähle einen Veröffentlichungstermin.': 'Choose a publishing time.',
  'Video-Ziele: gerendertes MP4': 'Video destinations: rendered MP4',
  'Bearbeite Bilder oder Clips, wähle das Format je Kanal und prüfe den Beitrag vor der Freigabe. Änderungen wirken erst nach dem Speichern.': 'Edit images or clips, choose a format for each channel and review the post before approval. Changes take effect after saving.',
  'Dein Weg zum Beitrag': 'Steps to publish',
  'Bilder oder Clip hinzufügen': 'Add images or a clip',
  'Kanal und Format wählen': 'Choose channel and format',
  'Text und Termin prüfen': 'Review text and schedule',
  'IG Feed und TikTok Foto(s): ein Bild bleibt ein Bildpost, ab zwei Bildern entsteht eine wischbare Bildserie. Reels, Story und TikTok Video erzeugen ein MP4.': 'IG Feed and TikTok photos: one image stays a single image post; two or more become a swipeable carousel. Reels, Stories and TikTok Video create an MP4.',
  'Vorschau der aktuellen Szene. Bildposts bleiben einzelne Bilder; nur gewählte Video-Ziele erhalten ein MP4. Texte lassen sich in der Vorschau ziehen.': 'Preview of the current scene. Image posts remain images; only selected video destinations receive an MP4. Drag text in the preview.',
  'Eigene Bilder verwenden': 'Use your own images',
  'Wähle ein Bild für einen einzelnen Bildpost oder mehrere Bilder für eine wischbare Galerie. Die Reihenfolge kannst du danach ändern.': 'Choose one image for a single post or multiple images for a swipeable carousel. You can change their order afterwards.',
  'Oder ein neues Bild beziehungsweise einen Clip generieren:': 'Or generate a new image or clip:',
  'Fehlgeschlagene Ziele erneut senden': 'Retry failed destinations',
  'teilweise veröffentlicht': 'partially published',
  'Bild nach links verschieben': 'Move image left',
  'Bild nach rechts verschieben': 'Move image right',
  '2 · Format wählen': '2 · Choose a format',
  'Ein Bild bleibt ein einzelner Bildpost. Ab zwei Bildern wird daraus eine wischbare Bildserie. Ein Video entsteht nur für ein Video-Ziel.': 'One image remains a single image post. Two or more become a swipeable carousel. A video is created only for a video destination.',
  'Einzelbild oder Karussell nach Bildanzahl': 'Single image or carousel based on image count',
  'Gerendertes MP4 für Reel, Story oder TikTok Video': 'Rendered MP4 for Reel, Story, or TikTok Video',
  'Bilder und Video': 'Images and video',
  'Beide Formen bewusst aus demselben Beitrag erzeugen': 'Create both formats from the same post',
  '1 Bild · Einzelbild': '1 image · single image post',
  '{n} Bilder · wischbares Karussell': '{n} images · swipeable carousel',
  'Noch kein Bild hinzugefügt': 'No image added yet',
  'Vorschau je Kanal': 'Preview by channel',
  'Änderungen zuerst speichern und die Vorschau neu rendern.': 'Save your changes and render the preview again.',
  'Gerenderte Medien erscheinen hier nach dem Vorschau-Render. Vorher siehst du die ausgewählten Originale.': 'Rendered media appears here after preview rendering. Before that, you see the selected originals.',
  'Einzelbild': 'Single image',
  '{n} Bilder zum Durchwischen': '{n} swipeable images',
  'Video als MP4': 'Video as MP4',
  'Bild {n}': 'Image {n}',
  'Video-Vorschau nach dem Rendern verfügbar.': 'Video preview available after rendering.',
  'Post selbst erstellen': 'Create a post yourself',
  'Beiträge': 'Posts',
  'AI-Inhalte': 'AI content',
  'Automatik': 'Automation',
  'Kanäle': 'Channels',
  'Du hast bereits ein Abo. Ein zweiter Checkout ist gesperrt. Dein bestehendes Abo kannst du unter „Guthaben & Tarif“ verwalten; ein Tarifwechsel ist derzeit nicht freigeschaltet.': 'You already have a subscription. A second checkout is blocked. You can manage your existing subscription under Billing & plan; switching plans is not available yet.',
  'Tarifwechsel derzeit nicht verfügbar': 'Plan changes are currently unavailable',
  'Ausgabenlimit': 'Spending limit',
  'Lege fest, wie viele Credits alle Projekte zusammen pro Tag höchstens verbrauchen dürfen. Guthaben kaufst du bei Bedarf oben manuell nach.': 'Set the maximum credits all projects may use per day. Buy more credits manually above when needed.',
  'Änderungen zuerst speichern.': 'Save your changes first.',
  '{platform} erlaubt höchstens {n} Bilder.': '{platform} allows at most {n} images.',
  'Für einen Bildpost sind zu viele Bilder ausgewählt. Prüfe die Plattformgrenze.': 'Too many images are selected for an image post. Check the platform limit.',
  'Instagram Feed': 'Instagram Feed',
  'Instagram Reel': 'Instagram Reel',
  'Foto(s)': 'Photo(s)',
  'Ein Bild als einzelner Bildpost, ab zwei Bildern als wischbares Karussell.': 'One image as a single post; two or more as a swipeable carousel.',
  'Gut für Produktfotos, Vorher/Nachher und Erklärungen in mehreren Bildern.': 'Great for product photos, before and after, and explanations across multiple images.',
  'Ein Video mit Bewegung oder Ton, kein Bilderkarussell.': 'A video with motion or sound, not an image carousel.',
  'Gut für Demos, Abläufe und kurze Einblicke mit Bewegung.': 'Great for demos, workflows, and short moving clips.',
  'Ein kurzes Video, das nach 24 Stunden verschwindet.': 'A short video that disappears after 24 hours.',
  'Gut für aktuelle Einblicke und zeitnahe Hinweise.': 'Great for timely updates and current glimpses.',
  'Ein Bild als Fotopost, ab zwei Bildern zum Durchwischen.': 'One image as a photo post; two or more to swipe through.',
  'Gut für Tipps in Schritten, Vergleiche und Bildstrecken.': 'Great for step by step tips, comparisons, and photo series.',
  'Ein gerendertes Video mit Bewegung oder Ton.': 'A rendered video with motion or sound.',
  'Gut für Vorführungen, Arbeitsschritte und bewegte Geschichten.': 'Great for demonstrations, workflows, and stories in motion.',
};

/**
 * Uebersetzt einen deutschen Satz -- oder gibt ihn zurueck, wie er ist.
 *
 * Zwei Formen:
 *   t('Speichern')                       -> "Save"
 *   t('{n} ausgewählt', { n: 3 })        -> "3 selected"
 */
export function t(text: string, werte?: Record<string, string | number>): string {
  const roh = aktuell === 'en' ? EN_BEREICHE[text] ?? EN[text] ?? text : text;
  if (!werte) return roh;
  return roh.replace(/\{(\w+)\}/g, (treffer, name: string) => (
    name in werte ? String(werte[name]) : treffer
  ));
}

/** Fuer Komponenten: zeichnet bei Sprachwechsel neu. */
export function useT(): typeof t {
  useSprache();
  return t;
}
